import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Dropdown,
	Grid,
	Icons,
	Inline,
	joinAttributes,
	Money,
	MoneySum,
	Select,
	Spinner,
	Stars,
	Stack,
	Table,
	TableDataCell,
	TableFooterDataCell,
	TableHeaderDataCell,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import {
	IAvailability,
	IAvailabilityRateOption,
	IAvailabilityRoomWiseRateOptions,
	IChildSlotOption,
	IHotelRemoteMealPlan,
	IHotelRemoteRoomType,
	TChildSlotOptions,
	THotelCancellationPolicy,
	THotelPaymentPolicy,
	useHotelAvailability,
} from "./store"
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import * as Validator from "yup"
import {
	EmptyNumberValidator,
	Form,
	FormSpy,
	GetFieldValue,
	SelectField,
	SelectInputField,
	SwitchInputField,
	TextInputField,
	getFormValueForKey,
	useFieldValue,
	useForm,
	useFormState,
	validateFormValues,
} from "@sembark-travel/ui-form"
import {
	computeRoomAllocationAndTaxForGivenRates,
	describeSelectionDetailsChange,
	detectStaleAvailability,
	distributeComplimentaryAcrossRooms,
	findMatchingRateOption,
	findMissingOccupancyRate,
	getChildSwapCandidates,
	getFreeChildAges,
	getBlockingPaxOverages,
	getNightlyPaxStatus,
	getExtraNightlyRate,
	getNightPriceBreakup,
	getRoomBasePrice,
	getSpareBedsByAllocation,
	getWorstNightPaxStatus,
	groupConsecutiveNights,
	getNightlyPricesForRoomGroup,
	getTotalPriceForRoomGroup,
	getUnassignedChildAges,
	groupPaxOveragesByConsecutiveNights,
	pickChildSlot,
	pickFreeChildSlots,
	TPaxOverageAllocation,
	TPaxOverageGroup,
	TChildSlotBucket,
	TChildSwapCandidate,
	TNightlyPaxOverage,
	TNightPriceBreakup,
	TNightPaxStatus,
	TPaxOverageRow,
	TRoomNightAllocation,
	TRoomNightPriceBreakdown,
	TTaxStrategyResult,
} from "./utils"
import pluralize from "pluralize"
import {
	addMoney,
	addMoneyFromDifferentCurrencies,
	addPercentageToMoney,
	createMoney,
	divideMoneyBy,
	formatMoneyByDecimal,
	moneyParseByDecimal,
	multipleMoneyBy,
	subtractMoney,
	TMoney,
} from "@sembark-travel/money"
import { TChildrenArray } from "../Tourists"
import { IHotel, THotelOtaRatePlan, THotelPaxTypeCode } from "../Hotels"
import {
	IDaywiseHotelPrice,
	// IHotelRemoteObpPriceBreakdown,
	THotelPriceBreakdowns,
} from "../utils"
import { INewQuoteState } from "../Trips/NewQuote"
import { IHotelRemoteConfig } from "../Trips"
import { TRateSheet } from "../RateSheets"
import { TOtaMealCategory } from "../HotelOtaMealPlans"

export type TNightlyPrice = {
	date: string
	rooms: Array<TRoomNightAllocation>
	rbp_breakdown?: Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>
	original_rbp_breakdown?: Extract<
		TRoomNightPriceBreakdown,
		{ pricing_type: "RBP" }
	>
	total: TMoney
	original_total: TMoney
	tax_savings?: TMoney
	tax_strategy?: "fill_base" | "round_robin"
	rate_sheet?: TRateSheet
	rate_plan?: THotelOtaRatePlan
}

export interface IConfirmedRoomSelection {
	room_type: IHotelRemoteRoomType
	meal_plan: IHotelRemoteMealPlan
	payment_policy: THotelPaymentPolicy
	cancellation_policy: THotelCancellationPolicy
	qty: number
	discount_percentage: number
	room_ids: Array<string>
	occupancy: {
		ppr: number
		aweb: number
		cwebAges: Array<number>
		cnbAges: Array<number>
		complimentaryAges: Array<number>
	}

	nightly_prices: Array<TNightlyPrice>
	total: TMoney // sum of nightly_prices totals
}

function toDecimal(money: TMoney): number {
	return Number(formatMoneyByDecimal(money))
}

function mapRbpBreakdownToRemote(
	breakdown: Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>,
	originalBreakdown: Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>,
	qty: number
): THotelPriceBreakdowns {
	const rows: THotelPriceBreakdowns = [
		{
			pax_type: breakdown.base.tier === "BASE" ? "BASE" : "A",
			occupancy: breakdown.base.tier,
			quantity: qty,
			calculated_rate_bt: toDecimal(breakdown.base.amount),
			original_rate_bt: toDecimal(originalBreakdown.base.amount),
			given_rate_bt: toDecimal(breakdown.base.amount),
		},
	]

	if (breakdown.aweb) {
		rows.push({
			pax_type: "AWEB",
			occupancy: "AWEB",
			quantity: breakdown.aweb.count,
			calculated_rate_bt: toDecimal(breakdown.aweb.rate),
			original_rate_bt: toDecimal(
				originalBreakdown.aweb?.rate ?? breakdown.aweb.rate
			),
			given_rate_bt: toDecimal(breakdown.aweb.rate),
		})
	}
	for (const c of breakdown.cweb?.children || []) {
		const original =
			originalBreakdown.cweb?.children.find((o) => o.age === c.age)?.rate ??
			c.rate
		rows.push({
			pax_type: "CWEB",
			occupancy: c.occupancy,
			quantity: 1,
			age: c.age,
			calculated_rate_bt: toDecimal(c.rate),
			original_rate_bt: toDecimal(original),
			given_rate_bt: toDecimal(c.rate),
		})
	}
	for (const c of breakdown.cnb?.children || []) {
		const original =
			originalBreakdown.cnb?.children.find((o) => o.age === c.age)?.rate ??
			c.rate
		rows.push({
			pax_type: "CNB",
			occupancy: c.occupancy,
			quantity: 1,
			age: c.age,
			calculated_rate_bt: toDecimal(c.rate),
			original_rate_bt: toDecimal(original),
			given_rate_bt: toDecimal(c.rate),
		})
	}
	for (const c of breakdown.complimentary?.children || []) {
		rows.push({
			pax_type: "COMP",
			occupancy: "COMP",
			quantity: 1,
			age: c.age,
			calculated_rate_bt: 0,
			original_rate_bt: 0,
			given_rate_bt: 0,
		})
	}

	return rows
}

const OCCUPANCY_PAX_LABELS: Record<"AWEB" | "CWEB" | "CNB", string> = {
	AWEB: "Adult with Extra Bed",
	CWEB: "Child with Extra Bed",
	CNB: "Child without Extra Bed",
}

const ROOM_ID_SUFFIX_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
// First 2 chars are the room's own 1-based number (zero-padded), last 2 are
// random — e.g. room index 2 (3rd room) -> "03MA".
function generateRoomId(roomIndex: number): string {
	const prefix = String(roomIndex + 1).padStart(2, "0")
	const suffix = Array.from(
		{ length: 2 },
		() =>
			ROOM_ID_SUFFIX_CHARS[
				Math.floor(Math.random() * ROOM_ID_SUFFIX_CHARS.length)
			]
	).join("")
	return `${prefix}${suffix}`
}

function mapRoomWiseBreakdownToRemote(
	rooms: Array<TRoomNightAllocation>,
	roomIds: Array<string>
):
	| Array<{
			room_key: string
			tax_percentage: number
			tax_amount: number
			total_amount_bt: number
			total_amount_at: number
			price_breakdowns: THotelPriceBreakdowns
	  }>
	| undefined {
	const rbpRooms = rooms.filter(
		(
			r
		): r is TRoomNightAllocation & {
			breakdown: Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>
			original_breakdown: Extract<
				TRoomNightPriceBreakdown,
				{ pricing_type: "RBP" }
			>
		} => r.breakdown.pricing_type === "RBP"
	)
	if (rbpRooms.length !== rooms.length) return undefined

	return rbpRooms.map((r) => {
		const taxAmount =
			r.breakdown.tax?.amount ?? createMoney(0, r.total.currency)
		return {
			room_key: roomIds[r.room_index],
			tax_percentage: r.breakdown.tax?.rate ?? 0,
			tax_amount: toDecimal(taxAmount),
			total_amount_bt: toDecimal(subtractMoney(r.total, taxAmount)),
			total_amount_at: toDecimal(r.total),
			price_breakdowns: mapRbpBreakdownToRemote(
				r.breakdown,
				r.original_breakdown,
				1
			),
		}
	})
}

function buildRoomStaysForGivenRates(
	newBreakdown: THotelPriceBreakdowns,
	taxResult: { winner: TTaxStrategyResult },
	occupancy: TGivenPriceOccupancy,
	constraints: TGivenPriceConstraints,
	previousRoomStays: ReturnType<typeof mapRoomWiseBreakdownToRemote>,
	currency: string
): ReturnType<typeof mapRoomWiseBreakdownToRemote> {
	const baseRow = findBaseBreakdownRow(newBreakdown)
	if (!baseRow) return undefined
	const awebRow = findBreakdownRow(newBreakdown, "AWEB")

	const complimentaryAges = newBreakdown
		.filter((r) => r.pax_type === "COMP")
		.map((r) => r.age)
		.filter((a): a is number => a !== undefined)
	const complimentaryDist = distributeComplimentaryAcrossRooms(
		occupancy.noOfRooms,
		complimentaryAges,
		taxResult.winner.distribution.map(
			(room) => room.cwebAges.length + room.cnbAges.length
		),
		constraints.maxChildOccupancy
	)

	return taxResult.winner.distribution.map((room, i) => {
		const rows: THotelPriceBreakdowns = [{ ...baseRow, quantity: 1 }]
		if (room.aweb > 0 && awebRow) {
			rows.push({ ...awebRow, quantity: room.aweb })
		}
		for (const age of room.cwebAges) {
			const row = findBreakdownRow(newBreakdown, "CWEB", age)
			if (row) rows.push({ ...row, quantity: 1 })
		}
		for (const age of room.cnbAges) {
			const row = findBreakdownRow(newBreakdown, "CNB", age)
			if (row) rows.push({ ...row, quantity: 1 })
		}
		for (const age of complimentaryDist[i] || []) {
			rows.push({
				pax_type: "COMP",
				occupancy: "COMP",
				quantity: 1,
				age,
				calculated_rate_bt: 0,
				original_rate_bt: 0,
				given_rate_bt: 0,
			})
		}

		const beforeTaxTotal = rows.reduce(
			(total, row) =>
				addMoney(
					total,
					multipleMoneyBy(
						moneyParseByDecimal(row.given_rate_bt, currency),
						row.quantity
					)
				),
			createMoney(0, currency)
		)
		const taxRate = taxResult.winner.roomTaxRates[i]
		const afterTax = addPercentageToMoney(beforeTaxTotal, taxRate)

		return {
			room_key: previousRoomStays?.[i]?.room_key ?? generateRoomId(i),
			tax_percentage: taxRate,
			tax_amount: toDecimal(subtractMoney(afterTax, beforeTaxTotal)),
			total_amount_bt: toDecimal(beforeTaxTotal),
			total_amount_at: toDecimal(afterTax),
			price_breakdowns: rows,
		}
	})
}

// function mapObpBreakdownToRemote(
// 	breakdown: Extract<TRoomNightPriceBreakdown, { pricing_type: "OBP" }>
// ): IHotelRemoteObpPriceBreakdown[number] {
// 	return {
// 		adult_tier: breakdown.adult_tier,
// 		adult_calculated: toDecimal(breakdown.adult_amount),
// 		adult_given: toDecimal(breakdown.adult_amount),
// 		...(breakdown.child_tier && breakdown.child_amount !== undefined
// 			? {
// 					child_tier: breakdown.child_tier,
// 					child_calculated: toDecimal(breakdown.child_amount),
// 					child_given: toDecimal(breakdown.child_amount),
// 				}
// 			: {}),
// 	}
// }

function mapNightlyPriceToDaywiseHotelPrice(
	roomType: IHotelRemoteRoomType,
	mealPlan: IHotelRemoteMealPlan,
	paymentPolicy: THotelPaymentPolicy,
	cancellationPolicy: THotelCancellationPolicy,
	nightly: IConfirmedRoomSelection["nightly_prices"][number],
	qty: number,
	discountPercent: number,
	roomIds: Array<string>
): IDaywiseHotelPrice {
	const base: Pick<
		IDaywiseHotelPrice,
		| "date"
		| "currency"
		| "price"
		| "given_price"
		| "original_price"
		| "remote_config"
	> = {
		date: parseDate(nightly.date),
		currency: nightly.total.currency,
		price: toDecimal(nightly.total),
		given_price: toDecimal(nightly.total),
		original_price: toDecimal(nightly.original_total),
		remote_config: {
			rate_sheet_id: nightly.rate_sheet?.id,
			ota_rate_plan_id: nightly.rate_plan?.id,
			ota_meal_plan_id: mealPlan.ota_meal_plan.id,
			ota_room_type_id: roomType.ota_room_type.id,
			base_adult_occupancy: roomType.ota_room_type.base_adult_occupancy,
			max_adult_occupancy: roomType.ota_room_type.max_adult_occupancy,
			max_child_occupancy: roomType.ota_room_type.max_child_occupancy,
			max_occupancy: roomType.ota_room_type.max_occupancy,
			payment_policy_id: paymentPolicy.id,
			cancellation_policy_id: cancellationPolicy.id,
			discount_percentage: discountPercent,
		},
	}

	if (nightly.rbp_breakdown) {
		return {
			...base,
			remote_pricing_type: "RBP",
			remote_price_breakdowns: mapRbpBreakdownToRemote(
				nightly.rbp_breakdown,
				nightly.original_rbp_breakdown ?? nightly.rbp_breakdown,
				qty
			),
			...(nightly.tax_strategy
				? { remote_tax_strategy: nightly.tax_strategy }
				: {}),
			remote_config: {
				...base.remote_config,
				room_stays: mapRoomWiseBreakdownToRemote(nightly.rooms, roomIds),
			},
		}
	}

	return {
		...base,
		remote_pricing_type: "OBP",
		// remote_obp_price_breakdown: nightly.rooms.map((r) => {
		// 	if (r.breakdown.pricing_type !== "OBP") {
		// 		throw new Error("Expected an OBP breakdown for a non-RBP night")
		// 	}
		// 	return mapObpBreakdownToRemote(r.breakdown)
		// }),
	}
}

function mapConfirmedSelectionToLineItem(selection: IConfirmedRoomSelection) {
	return {
		room_type: selection.room_type,
		meal_plan: selection.meal_plan,
		no_of_rooms: selection.qty,
		persons_per_room: selection.occupancy.ppr,
		adults_with_extra_bed: selection.occupancy.aweb,
		children_with_extra_bed: selection.occupancy.cwebAges.length,
		children_with_extra_bed_ages: selection.occupancy.cwebAges,
		children_without_extra_bed: selection.occupancy.cnbAges.length,
		children_without_extra_bed_ages: selection.occupancy.cnbAges,
		children_complimentary_ages: selection.occupancy.complimentaryAges,
		date_wise_prices: selection.nightly_prices.map((nightly) =>
			mapNightlyPriceToDaywiseHotelPrice(
				selection.room_type,
				selection.meal_plan,
				selection.payment_policy,
				selection.cancellation_policy,
				nightly,
				selection.qty,
				selection.discount_percentage,
				selection.room_ids
			)
		),
	}
}

function buildConfirmedSelections(
	availability: IAvailability,
	values: IHotelAvailabilityFormSchema,
	dateStrings: Array<string>,
	existingSelection: TExistingHotelSelection | undefined
): Array<IConfirmedRoomSelection> {
	const selections: Array<IConfirmedRoomSelection> = []
	const discountPercent = values.discount || 0

	for (const rt of availability.room_type_wise_rates) {
		const id = rt.room_type.id
		const qty = values[`qty_${id}`] || 0
		const selectedRatePlan = values[`rp_${id}`]
		if (qty <= 0 || !selectedRatePlan) continue

		const isEditOfExisting =
			existingSelection &&
			rt.room_type.ota_room_type.id ===
				existingSelection.remoteConfig.ota_room_type_id &&
			selectedRatePlan.rateOption.meal_plan.ota_meal_plan.id ===
				existingSelection.remoteConfig.ota_meal_plan_id
		const previousRoomIds = isEditOfExisting
			? (existingSelection.remoteConfig.room_stays || []).map((r) => r.room_key)
			: []
		const roomIds = Array.from(
			{ length: qty },
			(_, i) => previousRoomIds[i] ?? generateRoomId(i)
		)

		const ppr = values[`ppr_${id}`] || 1
		const aweb = values[`aweb_${id}`] || 0
		const cweb = values[`cweb_${id}`] || []
		const cnb = values[`cnb_${id}`] || []
		const complimentary = values[`complimentary_${id}`] || []
		const cwebAges = cweb.map((s) => s.age)
		const cnbAges = cnb.map((s) => s.age)
		const complimentaryAges = complimentary.map((s) => s.age)

		const nightlyPrices = getNightlyPricesForRoomGroup(
			selectedRatePlan.rateOption,
			rt.room_type,
			dateStrings,
			{ qty, ppr, aweb, cwebAges, cnbAges, complimentaryAges, discountPercent }
		)
		if (!nightlyPrices?.length) continue

		const total = addMoney(
			nightlyPrices[0].total,
			...nightlyPrices.slice(1).map((n) => n.total)
		)

		selections.push({
			room_type: rt.room_type,
			meal_plan: selectedRatePlan.rateOption.meal_plan,
			payment_policy: selectedRatePlan.rateOption.payment_policy,
			cancellation_policy: selectedRatePlan.rateOption.cancellation_policy,
			qty,
			discount_percentage: discountPercent,
			room_ids: roomIds,
			occupancy: { ppr, aweb, cwebAges, cnbAges, complimentaryAges },
			nightly_prices: nightlyPrices,
			total,
		})
	}

	return selections
}

// e.g. "30, 31 Oct, 1 Nov"
function formatNightDates(dateStrings: Array<string>): string {
	const months: Array<{ month: string; days: Array<string> }> = []
	for (const date of dateStrings) {
		const month = formatDate(date, "MMM")
		const last = months[months.length - 1]
		if (last && last.month === month) last.days.push(formatDate(date, "DD"))
		else months.push({ month, days: [formatDate(date, "DD")] })
	}
	return months.map((m) => `${m.days.join(", ")} ${m.month}`).join(", ")
}

const DISCOUNT_PRESETS = [0, 5, 10, 15]

function DiscountChip({
	selected,
	onClick,
	children,
}: {
	selected: boolean
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<Box
			as="button"
			type="button"
			onClick={onClick}
			borderWidth="1"
			borderColor={selected ? "primary" : "default"}
			bgColor={selected ? "primary" : "default"}
			color={selected ? "primary" : "default"}
			fontWeight="semibold"
			fontSize="sm"
			rounded="md"
			paddingX="3"
			paddingY="1"
			style={{ cursor: "pointer" }}
		>
			{children}
		</Box>
	)
}

function DiscountField() {
	const form = useForm<IHotelAvailabilityFormSchema>()
	const { value: discount } =
		useFieldValue<IHotelAvailabilityFormSchema["discount"]>("discount")
	const [showCustom, setShowCustom] = useState(
		() => !DISCOUNT_PRESETS.includes(discount || 0)
	)
	return (
		<Inline gap="3" alignItems="center" flexWrap="wrap">
			<Stack gap="px">
				<Text fontSize="sm" fontWeight="semibold">
					Discount
				</Text>
				<Text fontSize="xs" color="muted">
					Applied to base room rate only
				</Text>
			</Stack>
			<Inline gap="1">
				{DISCOUNT_PRESETS.map((preset) => (
					<DiscountChip
						key={preset}
						selected={!showCustom && (discount || 0) === preset}
						onClick={() => {
							setShowCustom(false)
							form.change("discount", preset)
						}}
					>
						{preset}%
					</DiscountChip>
				))}
				<DiscountChip selected={showCustom} onClick={() => setShowCustom(true)}>
					Custom
				</DiscountChip>
			</Inline>
			{showCustom ? (
				<SelectInputField
					name="discount"
					onChange={(e) => {
						form.change("discount", Number(e.currentTarget.value))
					}}
				>
					{Array.from({ length: 101 }, (_, i) => (
						<option key={i} value={i}>
							{i}%
						</option>
					))}
				</SelectInputField>
			) : null}
		</Inline>
	)
}

function AvailabilityHeaderCard({
	hotel,
	dateStrings,
	tripNoOfAdults,
	tripChildren,
}: {
	hotel: IHotel
	dateStrings: Array<string>
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
}) {
	const childAges = (tripChildren || [])
		.flatMap((c) => Array.from({ length: c.count }, () => c.age))
		.sort((a, b) => b - a)
	return (
		<Inline
			gap="4"
			justifyContent="between"
			flexWrap="wrap"
			bgColor="accent"
			borderWidth="1"
			borderColor="accent"
			rounded="md"
			paddingX="3"
			paddingY="2"
			color="muted"
		>
			<Stack gap="2">
				<Text fontSize="sm">
					{joinAttributes(
						<>
							<Icons.Calendar /> {formatNightDates(dateStrings)}
						</>,
						pluralize("Night", dateStrings.length, true)
					)}
				</Text>
				{tripNoOfAdults || childAges.length ? (
					<Text fontSize="sm">
						{joinAttributes(
							<>
								<Icons.Users /> {tripNoOfAdults || 0}A {childAges.length}C
							</>,
							[
								childAges.length > 0,
								<>
									<Icons.Child /> {childAges.map((age) => `${age}y`).join(", ")}
								</>,
							]
						)}
					</Text>
				) : null}
			</Stack>
			<Stack gap="2" textAlign="right">
				<Text fontSize="sm" fontWeight="semibold">
					<Icons.Bed /> {hotel.name}
				</Text>
				{hotel.location || hotel.stars ? (
					<Text fontSize="sm" color="muted">
						{joinAttributes(
							[
								Boolean(hotel.location),
								<>
									<Icons.LocationMarker /> {hotel.location?.short_name}
								</>,
							],
							[
								Boolean(hotel.stars),
								hotel.stars ? (
									<Stars stars={hotel.stars} showSingleStar />
								) : null,
							]
						)}
					</Text>
				) : null}
			</Stack>
		</Inline>
	)
}

export function HotelAvailabilityInDialog({
	hotel,
	dates,
	tripId,
	tripChildren,
	tripNoOfAdults,
	onConfirm,
	existingSelection,
	getOtherRows,
	children,
}: {
	hotel: IHotel
	dates: Array<Date>
	tripId: number
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
	onConfirm: (selections: Array<IConfirmedRoomSelection>) => void
	existingSelection?: TExistingHotelSelection

	getOtherRows: () => Array<TPaxOverageRow>
	children: (props: { setDialogOpen: () => void }) => React.ReactNode
}) {
	const [dialogOpen, openDialog, closeDialog] = useDialog()
	const [paxOverages, setPaxOverages] = useState<Array<TNightlyPaxOverage>>([])
	const datesKey = dates.map((d) => formatDate(d, "YYYY-MM-DD")).join(",")
	const dateStrings = useMemo(
		() => (datesKey ? datesKey.split(",") : []),
		[datesKey]
	)
	// snapshot of the quote's other rows while the dialog is open
	const otherRows = useMemo(
		() => (dialogOpen ? getOtherRows() : []),
		[dialogOpen, getOtherRows]
	)
	const { data, error, isValidating, mutate } = useHotelAvailability(
		dialogOpen ? hotel.id : undefined,
		dateStrings,
		tripChildren,
		tripId
	)

	const availability = data?.data
	const childSlotOptions = data?.meta?.child_slot_options

	const paxPlacement = useMemo<TPaxPlacementContext | null>(
		() =>
			availability && childSlotOptions
				? {
						tripNoOfAdults,
						tripChildren,
						otherRows,
						hotelName: hotel.name,
						availability,
						childSlotOptions,
						dateStrings,
					}
				: null,
		[
			tripNoOfAdults,
			tripChildren,
			otherRows,
			hotel.name,
			availability,
			childSlotOptions,
			dateStrings,
		]
	)

	const isFetching = (!availability && !error) || isValidating

	return (
		<>
			{children({ setDialogOpen: openDialog })}

			<Dialog
				open={dialogOpen}
				onClose={closeDialog}
				title="Check Availability"
				lg
			>
				<Dialog.Body>
					{isFetching ? (
						<Box textAlign="center" padding="4">
							<Spinner /> <Text>Fetching availability…</Text>
						</Box>
					) : error ? (
						<Alert status="error">
							<Stack gap="2">
								<Text>Could not fetch availability. Please try again.</Text>
								<Button onClick={() => mutate()}>Retry</Button>
							</Stack>
						</Alert>
					) : !availability?.room_type_wise_rates?.length ||
					  !childSlotOptions ? (
						<Alert>No rooms available for these dates.</Alert>
					) : (
						<ConfirmationDialogProvider>
							{({ confirm }) => (
								<Stack gap="3">
									<AvailabilityHeaderCard
										hotel={hotel}
										dateStrings={dateStrings}
										tripNoOfAdults={tripNoOfAdults}
										tripChildren={tripChildren}
									/>
									<PaxPlacementContext.Provider value={paxPlacement}>
										<HotelAvailabilityForm
											availability={availability}
											childSlotOptions={childSlotOptions}
											dateStrings={dateStrings}
											onSubmit={async (selections) => {
												const overages = getBlockingPaxOverages({
													tripNoOfAdults,
													tripChildren,
													dateStrings,
													existingRows: otherRows,
													hotelName: hotel.name,
													selections,
												})
												if (overages.length) {
													// blocking: the user has to fix the rooms first
													setPaxOverages(overages)
													await confirm()
													return
												}
												onConfirm(selections)
												closeDialog()
											}}
											onCancel={closeDialog}
											hotel={hotel}
											existingSelection={existingSelection}
										/>
									</PaxPlacementContext.Provider>
									<ConfirmationDialog
										title="Rooms don't match the trip's guests"
										rejectActionLabel="Go back & fix"
										hideAcceptAction
										size="md"
									>
										<PaxOveragesWarning
											overages={paxOverages}
											tripNoOfAdults={tripNoOfAdults}
											tripChildren={tripChildren}
										/>
									</ConfirmationDialog>
								</Stack>
							)}
						</ConfirmationDialogProvider>
					)}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function PaxOveragesWarning({
	overages,
	tripNoOfAdults,
	tripChildren,
}: {
	overages: Array<TNightlyPaxOverage>
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
}) {
	const tripTotal = overages[0]?.tripTotal || 0
	const groups = groupPaxOveragesByConsecutiveNights(overages)
	// extra pax with several rooms sharing one Base Pax, e.g. 3 adults in 2 × 2
	const showSeparateRowHint = overages.some(
		(o) =>
			o.accommodated > o.tripTotal &&
			o.allocations.some(
				(a) => a.source === "adding" && a.rooms > 1 && a.ppr > 1
			)
	)
	return (
		<Stack gap="4">
			<TripPaxStrip
				tripNoOfAdults={tripNoOfAdults}
				tripChildren={tripChildren}
				tripTotal={tripTotal}
			/>
			<Stack gap="3">
				{groups.map((group) => (
					<PaxOverageNightCard key={group.dates[0]} group={group} />
				))}
			</Stack>
			{showSeparateRowHint ? (
				<Alert status="info">
					Need a different number of adults per room (e.g. one double + one
					single)? Add the other room as a separate hotel row with its own Base
					Pax.
				</Alert>
			) : null}
			<Text fontSize="sm" color="muted">
				Fix the rooms on these nights to continue.
			</Text>
		</Stack>
	)
}

function TripPaxStrip({
	tripNoOfAdults,
	tripChildren,
	tripTotal,
}: {
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
	tripTotal: number
}) {
	const childAges = (tripChildren || [])
		.flatMap((c) => Array.from({ length: c.count }, () => c.age))
		.sort((a, b) => b - a)
	return (
		<Inline
			gap="3"
			alignItems="center"
			justifyContent="between"
			bgColor="subtle"
			rounded="md"
			paddingX="3"
			paddingY="2"
		>
			<Inline gap="2" alignItems="center" flexWrap="wrap">
				<Text fontSize="sm" color="muted">
					Trip
				</Text>
				{tripNoOfAdults ? (
					<Badge>
						<Icons.User /> {pluralize("Adult", tripNoOfAdults, true)}
					</Badge>
				) : null}
				{childAges.map((age, i) => (
					<Badge key={i}>Child {age}y</Badge>
				))}
			</Inline>
			<Text fontWeight="semibold" whiteSpace="nowrap">
				{pluralize("guest", tripTotal, true)}
			</Text>
		</Inline>
	)
}

function getNightsRangeLabel(dates: Array<string>): string {
	const first = dates[0]
	const last = dates[dates.length - 1]
	if (!first || !last) return ""
	return first === last
		? formatDate(first, "ddd, DD MMM")
		: `${formatDate(first, "ddd, DD MMM")} – ${formatDate(last, "ddd, DD MMM")}`
}

function PaxOverageNightCard({ group }: { group: TPaxOverageGroup }) {
	const { dates, accommodated, tripTotal, overAges, allocations } = group
	const extra = accommodated - tripTotal
	const existing = allocations.filter((a) => a.source === "existing")
	const adding = allocations.filter((a) => a.source === "adding")
	const overAgeSet = new Set(overAges.map((a) => a.age))
	return (
		<Stack borderWidth="1" rounded="md" padding="3" gap="3">
			<Inline gap="2" alignItems="baseline">
				<Text fontWeight="semibold">{getNightsRangeLabel(dates)}</Text>
				<Text fontSize="sm" color="muted">
					· {pluralize("night", dates.length, true)}
				</Text>
			</Inline>
			<Stack gap="1" bgColor="danger" rounded="md" paddingX="3" paddingY="2">
				{extra > 0 ? (
					<Text fontSize="sm" color="danger">
						<Text as="span" fontWeight="semibold">
							{pluralize("extra guest", extra, true)}:
						</Text>{" "}
						the rooms hold {accommodated}, but the trip has {tripTotal} only.
					</Text>
				) : null}
				{overAges.map((a) => (
					<Text key={a.age} fontSize="sm" color="danger">
						<Text as="span" fontWeight="semibold">
							Child {a.age}y is added {getTimesLabel(a.placed)}:
						</Text>{" "}
						{getTripChildrenOfAgeLabel(a.age, a.inTrip)}.
					</Text>
				))}
			</Stack>
			{existing.length ? (
				<PaxAllocationSection
					title="Already in quote"
					allocations={existing}
					overAgeSet={overAgeSet}
				/>
			) : null}
			<PaxAllocationSection
				title="Adding now"
				allocations={adding}
				overAgeSet={overAgeSet}
			/>
		</Stack>
	)
}

function getTimesLabel(count: number): string {
	return count === 2 ? "twice" : `${count} times`
}

function getTripChildrenOfAgeLabel(age: number, count: number): string {
	if (!count) return `the trip has no ${age}y child`
	return count === 1
		? `the trip has only one ${age}y child`
		: `the trip has ${count} children of ${age}y`
}

function PaxAllocationSection({
	title,
	allocations,
	overAgeSet,
}: {
	title: string
	allocations: Array<TPaxOverageAllocation>
	overAgeSet: Set<number>
}) {
	return (
		<Stack gap="2">
			<Text
				fontSize="sm"
				fontWeight="semibold"
				color="muted"
				textTransform="uppercase"
			>
				{title}
			</Text>
			{allocations.map((a, i) => (
				<PaxAllocationLine key={i} allocation={a} overAgeSet={overAgeSet} />
			))}
		</Stack>
	)
}

function PaxAllocationLine({
	allocation: a,
	overAgeSet,
}: {
	allocation: TPaxOverageAllocation
	overAgeSet: Set<number>
}) {
	const childChips = [
		...a.cwebAges.map((age) => ({ age, label: "extra bed" })),
		...a.cnbAges.map((age) => ({ age, label: "no bed" })),
		...a.complimentaryAges.map((age) => ({ age, label: "complimentary" })),
	]
	return (
		<Stack gap="1" paddingLeft="4">
			<Inline gap="2" alignItems="center" justifyContent="between">
				<Text fontSize="sm" fontWeight="semibold">
					{[a.hotelName, a.roomTypeName, a.mealPlanName]
						.filter(Boolean)
						.join(" · ")}
				</Text>
				<Text fontSize="sm" color="muted" whiteSpace="nowrap">
					{pluralize("room", a.rooms, true)} ·{" "}
					{pluralize("guest", a.heads, true)}
				</Text>
			</Inline>
			<Inline gap="1" alignItems="center" flexWrap="wrap">
				<Badge>
					{a.ppr
						? `${pluralize("Adult", a.ppr, true)}/room × ${pluralize("room", a.rooms, true)}`
						: pluralize("room", a.rooms, true)}
				</Badge>
				{Array.from({ length: a.aweb }, (_, i) => (
					<Badge key={`aweb-${i}`}>Adult · extra bed</Badge>
				))}
				{childChips.map((c, i) => (
					<Badge
						key={i}
						status={overAgeSet.has(c.age) ? "danger" : undefined}
						title={overAgeSet.has(c.age) ? "Added more than once" : undefined}
					>
						Child {c.age}y · {c.label}
					</Badge>
				))}
			</Inline>
		</Stack>
	)
}

export type TExistingHotelSelection = {
	roomType: IHotelRemoteRoomType
	mealPlan: IHotelRemoteMealPlan
	remoteConfig: IHotelRemoteConfig
	noOfRooms: number
	personsPerRoom: number
	aweb: number
	cwebAges: Array<number>
	cnbAges: Array<number>
	complimentaryAges: Array<number>
}

function matchAgesToOptions(
	storedAges: Array<number>,
	preferredOptions: Array<IChildSlotOption>,
	fallbackOptions: Array<Array<IChildSlotOption>>,
	usedIds: Set<number>,
	reclassifiedIds: Set<number>
): { matched: Array<IChildSlotOption>; droppedAges: Array<number> } {
	const matched: Array<IChildSlotOption> = []
	const droppedAges: Array<number> = []
	for (const age of storedAges) {
		let found = preferredOptions.find(
			(o) => o.age === age && !usedIds.has(o.id)
		)
		if (!found) {
			for (const options of fallbackOptions) {
				found = options.find((o) => o.age === age && !usedIds.has(o.id))
				if (found) {
					reclassifiedIds.add(found.id)
					break
				}
			}
		}
		if (found) {
			usedIds.add(found.id)
			matched.push(found)
		} else {
			droppedAges.push(age)
		}
	}
	return { matched, droppedAges }
}

function resolvePrepopulatedChildSlots(
	existingSelection: TExistingHotelSelection,
	childSlotOptions: TChildSlotOptions
): {
	cweb: Array<IChildSlotOption>
	cnb: Array<IChildSlotOption>
	complimentary: Array<IChildSlotOption>
	reclassifiedIds: Set<number>
	droppedAges: Array<number>
} {
	const options = childSlotOptions || { complimentary: [], cweb: [], cnb: [] }
	const usedIds = new Set<number>()
	const reclassifiedIds = new Set<number>()

	const cwebResult = matchAgesToOptions(
		existingSelection.cwebAges,
		options.CWEB,
		[options.CNB, options.COMP],
		usedIds,
		reclassifiedIds
	)
	const cnbResult = matchAgesToOptions(
		existingSelection.cnbAges,
		options.CNB,
		[options.CWEB, options.COMP],
		usedIds,
		reclassifiedIds
	)
	const complimentaryResult = matchAgesToOptions(
		existingSelection.complimentaryAges,
		options.COMP,
		[options.CWEB, options.CNB],
		usedIds,
		reclassifiedIds
	)

	return {
		cweb: cwebResult.matched,
		cnb: cnbResult.matched,
		complimentary: complimentaryResult.matched,
		reclassifiedIds,
		droppedAges: [
			...cwebResult.droppedAges,
			...cnbResult.droppedAges,
			...complimentaryResult.droppedAges,
		],
	}
}

export interface IGlobalMealPlanOption {
	id: number // ota_meal_plan.id — stable across room types, unlike meal_plan.id
	name: string // ota meal plan name, not the rate option's
	category: TOtaMealCategory | null
}

export interface IHotelAvailabilityFormSchema {
	discount: number
	global_meal_plan: IGlobalMealPlanOption | null
	[key: `qty_${number}`]: number
	[key: `rp_${number}`]: IRateOption | undefined
	[key: `ppr_${number}`]: number
	[key: `aweb_${number}`]: number
	[key: `cweb_${number}`]: Array<IChildSlotOption>
	[key: `cnb_${number}`]: Array<IChildSlotOption>
	[key: `complimentary_${number}`]: Array<IChildSlotOption>
}

function HotelAvailabilityForm({
	hotel,
	availability,
	childSlotOptions,
	dateStrings,
	existingSelection,
	onSubmit,
	onCancel,
}: {
	hotel: IHotel
	availability: IAvailability
	childSlotOptions: TChildSlotOptions
	dateStrings: Array<string>
	existingSelection?: TExistingHotelSelection
	onSubmit: (selections: Array<IConfirmedRoomSelection>) => void
	onCancel: () => void
}) {
	const mealPlanOptions = useMemo<Array<IGlobalMealPlanOption>>(() => {
		const seen = new Set<number>()
		const options: Array<IGlobalMealPlanOption> = []
		for (const rt of availability.room_type_wise_rates) {
			for (const option of rt.rate_options) {
				const otaId = option.meal_plan.ota_meal_plan.id
				if (seen.has(otaId)) continue
				seen.add(otaId)
				options.push({
					id: otaId,
					name: option.meal_plan.ota_meal_plan.name,
					category: option.meal_plan.ota_meal_plan.meal_category,
				})
			}
		}
		return options
	}, [availability])

	const {
		resolvedInitialValues,
		reclassifiedChildSlotIds,
		droppedChildAges,
		selectionChange,
		selectionNotFound,
	} = useMemo(() => {
		const defaultMealPlan =
			mealPlanOptions.find((mp) => mp.name === "CP") || mealPlanOptions.length
				? mealPlanOptions[0]
				: null

		const initalValues: IHotelAvailabilityFormSchema = {
			discount: existingSelection?.remoteConfig.discount_percentage ?? 0,
			global_meal_plan: defaultMealPlan,
		}
		availability.room_type_wise_rates.forEach((item) => {
			initalValues[`qty_${item.room_type.id}`] = 0
		})

		let reclassifiedChildSlotIds: Set<number> | undefined
		let droppedChildAges: Array<number> = []
		let selectionChange: string | undefined
		let selectionNotFound = false

		if (existingSelection) {
			const matched = findMatchingRateOption(
				availability,
				existingSelection.remoteConfig.ota_room_type_id,
				existingSelection.remoteConfig.ota_meal_plan_id
			)

			if (matched) {
				const { roomTypeWiseRate: matchedRoomType, rateOption } = matched
				const id = matchedRoomType.room_type.id
				initalValues[`qty_${id}`] = existingSelection.noOfRooms

				const matchedRatePlan: IRateOption = {
					id: rateOption.meal_plan.id,
					name: rateOption.name,
					rateOption,
				}
				initalValues[`rp_${id}`] = matchedRatePlan
				initalValues[`ppr_${id}`] = existingSelection.personsPerRoom
				initalValues[`aweb_${id}`] = existingSelection.aweb

				selectionChange = describeSelectionDetailsChange({
					savedRoomType: existingSelection.roomType,
					currentRoomType: matchedRoomType.room_type,
					savedMealPlan: existingSelection.mealPlan,
					currentMealPlan: rateOption.meal_plan,
				})

				const { cweb, cnb, complimentary, reclassifiedIds, droppedAges } =
					resolvePrepopulatedChildSlots(existingSelection, childSlotOptions)
				initalValues[`cweb_${id}`] = cweb
				initalValues[`cnb_${id}`] = cnb
				initalValues[`complimentary_${id}`] = complimentary
				reclassifiedChildSlotIds = reclassifiedIds
				droppedChildAges = droppedAges
			} else {
				selectionNotFound = true
			}
		}

		return {
			resolvedInitialValues: initalValues,
			reclassifiedChildSlotIds,
			droppedChildAges,
			selectionChange,
			selectionNotFound,
		}
	}, [availability, existingSelection, childSlotOptions, mealPlanOptions])

	return (
		<Form<IHotelAvailabilityFormSchema>
			onSubmit={(values) => {
				const selections = buildConfirmedSelections(
					availability,
					values,
					dateStrings,
					existingSelection
				)

				if (selections.length > 0) {
					onSubmit(selections)
				}
			}}
			initialValues={resolvedInitialValues}
		>
			{({ handleSubmit, form }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Stack gap="2">
							{selectionNotFound ? (
								<Alert status="warning">
									The previously selected room type or meal plan is no longer
									available for this hotel — please make a new selection.
								</Alert>
							) : null}
							{selectionChange ? (
								<Alert status="warning">
									{selectionChange} It's selected below with the latest rates.
									Review and confirm to update the quote.
								</Alert>
							) : null}
							<PaxAlreadyPlacedBanner />
							{droppedChildAges.length ? (
								<Alert status="warning">
									{pluralize("child", droppedChildAges.length, true)} (
									{droppedChildAges.map((age) => `${age}y`).join(", ")}) from
									the previous selection could not be matched and{" "}
									{droppedChildAges.length === 1 ? "wasn't" : "weren't"}{" "}
									re-added — please review.
								</Alert>
							) : null}
							<Box
								paddingX="3"
								paddingY="2"
								borderWidth="1"
								rounded="md"
								bgColor="subtle"
							>
								<Inline
									gap="4"
									alignItems="center"
									justifyContent="between"
									flexWrap="wrap"
								>
									<Inline gap="2" alignItems="center">
										<Text fontSize="sm" fontWeight="semibold">
											Meal Plan
										</Text>
										<SelectField
											select={Select}
											name="global_meal_plan"
											options={mealPlanOptions}
											searchable={false}
											placeholder="Select a meal plan."
										/>
									</Inline>
									<DiscountField />
								</Inline>
							</Box>
							<Table responsive bordered>
								<thead>
									<tr>
										<TableHeaderDataCell>Room Type</TableHeaderDataCell>
										<TableHeaderDataCell>Availability</TableHeaderDataCell>
										<BaseRateHeaderCell />
										<TableHeaderDataCell></TableHeaderDataCell>
									</tr>
								</thead>
								<tbody>
									{availability.room_type_wise_rates.map((rt) => (
										<RoomTypeWiseRate
											key={rt.room_type.id}
											roomTypeWiseRate={rt}
											dateStrings={dateStrings}
											childSlotOptions={childSlotOptions}
											reclassifiedChildSlotIds={reclassifiedChildSlotIds}
										/>
									))}
								</tbody>
							</Table>

							<GrandTotal
								availability={availability}
								dateStrings={dateStrings}
							/>

							<Box paddingY="2" borderTopWidth="1">
								<FormSpy<IHotelAvailabilityFormSchema>
									subscription={{ values: true }}
								>
									{({ values }) => {
										let selectedCount = 0
										let allHaveRatePlan = true
										for (const rt of availability.room_type_wise_rates) {
											const id = rt.room_type.id
											const qty = values[`qty_${id}`] || 0
											if (qty > 0) {
												selectedCount++
												if (!values[`rp_${id}`]) allHaveRatePlan = false
											}
										}
										const canConfirm = selectedCount > 0 && allHaveRatePlan
										return (
											<Stack gap="2">
												{selectedCount > 0 && !allHaveRatePlan ? (
													<Text fontSize="sm" color="warning">
														Please select a rate plan for every room type you
														want to add.
													</Text>
												) : null}
												<Inline
													gap="4"
													justifyContent={"end"}
													alignItems="center"
													flexWrap="wrap"
												>
													<PaxTotalCounter />
													<Inline gap="2">
														<Button onClick={onCancel} level="tertiary">
															Cancel
														</Button>
														<Button type="submit" disabled={!canConfirm}>
															Confirm{" "}
															{selectedCount > 0
																? `(${selectedCount} ${pluralize("room type", selectedCount)})`
																: "Selection"}
														</Button>
													</Inline>
												</Inline>
											</Stack>
										)
									}}
								</FormSpy>
							</Box>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

function QuantityControl({
	value,
	min,
	max,
	onChange,
	disabled,
	disableDecrement,
	disableIncrement,
	decrementTooltip,
	incrementTooltip,
}: {
	value: number
	min: number
	max: number
	onChange: (value: number) => void
	disabled?: boolean
	disableDecrement?: boolean
	disableIncrement?: boolean
	decrementTooltip?: React.ReactNode
	incrementTooltip?: React.ReactNode
}) {
	return (
		<Inline gap="1" alignItems="center">
			<Tooltip content={decrementTooltip}>
				<Button
					size="sm"
					level="tertiary"
					disabled={disabled || disableDecrement || value <= min}
					onClick={() => onChange(Math.max(min, value - 1))}
				>
					<Icons.MinusCircle />
				</Button>
			</Tooltip>
			<Text
				style={{ width: "20px", textAlign: "center" }}
				fontWeight="semibold"
			>
				{value}
			</Text>
			<Tooltip content={incrementTooltip}>
				<Button
					size="sm"
					level="tertiary"
					disabled={disabled || disableIncrement || value >= max}
					onClick={() => onChange(Math.min(max, value + 1))}
				>
					<Icons.PlusCircle />
				</Button>
			</Tooltip>
		</Inline>
	)
}

/**
 * @returns It is the base fact-finder — for each date, scans every
   rate_slot for that room type and takes the minimum available_rooms across them
   (since a room type can have multiple rate plans, each independently reporting
   availability — you can only actually sell as many rooms as the most
   constrained plan allows).
 */
function getAvailabilityBreakdown(
	roomType: IAvailabilityRoomWiseRateOptions,
	dateStrings: Array<string>
): Array<{ date: string; available_rooms: number }> {
	return dateStrings.map((date) => {
		let min = Infinity
		for (const option of roomType.rate_options) {
			const entry = option.dates.find((d) => d.date_local === date)
			if (entry) {
				min = Math.min(min, entry.available_rooms)
			}
		}
		return { date, available_rooms: min === Infinity ? 0 : min }
	})
}

/**
 *
 * @returns It just returns a single number for avaialbility badge which represents a minimum avaiable rooms you can book across the selected nights.
 */
function getMinAvailableRooms(
	roomType: IAvailabilityRoomWiseRateOptions,
	dateStrings: Array<string>
): number {
	const breakdown = getAvailabilityBreakdown(roomType, dateStrings)
	if (!breakdown.length) return 0
	return Math.min(...breakdown.map((b) => b.available_rooms))
}

function getMinAvailableRoomsForOption(
	option: IAvailabilityRateOption,
	dateStrings: Array<string>
): number {
	if (!dateStrings.length) return 0
	return Math.min(
		...dateStrings.map(
			(date) =>
				option.dates.find((d) => d.date_local === date)?.available_rooms ?? 0
		)
	)
}

/**
 * Night-wise availability and base rate of a rate option. The rate follows the
 * chosen Base Pax tier once the room is configured, after discount.
 */
function RateOptionAvailabilityTooltip({
	rateOption,
	dateStrings,
	ppr,
}: {
	rateOption: IAvailabilityRateOption
	dateStrings: Array<string>
	ppr: number
}) {
	const { value: discount } =
		useFieldValue<IHotelAvailabilityFormSchema["discount"]>("discount")
	const discountFactor = (100 - (discount || 0)) / 100
	const nights = dateStrings.map((date) => ({
		date,
		availableRooms:
			rateOption.dates.find((d) => d.date_local === date)?.available_rooms ?? 0,
		rate: getRoomBasePrice(rateOption, ppr, date)?.amount,
		discountedRate: getRoomBasePrice(rateOption, ppr, date, discountFactor)
			?.amount,
	}))
	const lowest = Math.min(...nights.map((n) => n.availableRooms))
	return (
		<Tooltip
			interactive
			content={
				<Stack gap="2">
					<Text fontSize="sm" fontWeight="semibold">
						Availability &amp; Rates – {rateOption.meal_plan.ota_meal_plan.name}
					</Text>
					<Stack gap="1">
						<Inline gap="4" borderBottomWidth="1" paddingBottom="1">
							<Box flex="1">
								<Text fontSize="xs" fontWeight="semibold" color="muted">
									Date
								</Text>
							</Box>
							<Text
								fontSize="xs"
								fontWeight="semibold"
								color="muted"
								style={{ width: "56px", textAlign: "right" }}
							>
								Avail.
							</Text>
							<Text
								fontSize="xs"
								fontWeight="semibold"
								color="muted"
								style={{ width: "120px", textAlign: "right" }}
							>
								Base Rate
							</Text>
						</Inline>
						{nights.map((n) => (
							<Inline key={n.date} gap="4" alignItems="center">
								<Box flex="1">
									<Text fontSize="sm">{formatDate(n.date, "D MMM")}</Text>
								</Box>
								<Text
									fontSize="sm"
									fontWeight={
										n.availableRooms === lowest ? "semibold" : undefined
									}
									color={n.availableRooms <= 0 ? "danger" : undefined}
									style={{ width: "56px", textAlign: "right" }}
								>
									{n.availableRooms <= 0 ? "Sold out" : n.availableRooms}
								</Text>
								<Text
									fontSize="sm"
									fontWeight="semibold"
									style={{ width: "120px", textAlign: "right" }}
								>
									{n.rate && n.discountedRate ? (
										<>
											{discount ? (
												<Text
													as="span"
													fontWeight="normal"
													color="muted"
													style={{ textDecoration: "line-through" }}
												>
													<Money money={n.rate} />{" "}
												</Text>
											) : null}
											<Money money={n.discountedRate} />{" "}
											<Text as="span" fontSize="xs" fontWeight="normal">
												+ Tax
											</Text>
										</>
									) : (
										"—"
									)}
								</Text>
							</Inline>
						))}
					</Stack>
				</Stack>
			}
		>
			<Box
				as="span"
				display="inlineFlex"
				color="accent"
				marginLeft="1"
				style={{ cursor: "help", verticalAlign: "middle" }}
			>
				<Icons.Info />
			</Box>
		</Tooltip>
	)
}

function BaseRateHeaderCell() {
	const { value: globalMealPlan } =
		useFieldValue<IHotelAvailabilityFormSchema["global_meal_plan"]>(
			"global_meal_plan"
		)
	return (
		<TableHeaderDataCell>
			Base Rate
			{globalMealPlan
				? ` (${globalMealPlan.category || globalMealPlan.name})`
				: ""}
		</TableHeaderDataCell>
	)
}

/**
 * Average per-night flat BASE rate for a rate slot, pre-tax — used purely as
 * a preview/hint (room-type row, rate-option dropdown) before any occupancy is
 * picked. Always the flat "BASE" pax_type, never a tiered A-count rate,
 * since no persons-per-room is known yet at preview time.
 */
function getAvgBaseRate(
	rateOption: IAvailabilityRateOption
): TMoney | undefined {
	const rates = rateOption.dates
		.map(
			(d) => d.rates.find((r) => r.pax_type === "BASE")?.rate_before_tax_money
		)
		.filter((r): r is TMoney => Boolean(r))
	if (!rates.length) return undefined
	return divideMoneyBy(
		addMoney(rates[0], ...rates.slice(1)),
		rateOption.dates.length
	)
}

function RoomTypeWiseRate({
	roomTypeWiseRate,
	dateStrings,
	childSlotOptions,
	reclassifiedChildSlotIds,
}: {
	roomTypeWiseRate: IAvailabilityRoomWiseRateOptions
	dateStrings: Array<string>
	childSlotOptions?: TChildSlotOptions
	reclassifiedChildSlotIds?: Set<number>
}) {
	const form = useForm<IHotelAvailabilityFormSchema>()

	const id = roomTypeWiseRate.room_type.id
	const fieldName = `qty_${id}` as const
	const rpFieldName = `rp_${id}` as const

	const { value: quantity } = useFieldValue<number>(fieldName)
	const { value: selectedRatePlan } =
		useFieldValue<IHotelAvailabilityFormSchema[`rp_${number}`]>(rpFieldName)
	const { value: ppr } = useFieldValue<
		IHotelAvailabilityFormSchema[`ppr_${number}`]
	>(`ppr_${id}` as const)
	const { value: globalMealPlan } =
		useFieldValue<IHotelAvailabilityFormSchema["global_meal_plan"]>(
			"global_meal_plan"
		)

	const qty = quantity || 0

	const maxOverallRooms = useMemo(
		() => getMinAvailableRooms(roomTypeWiseRate, dateStrings),
		[roomTypeWiseRate, dateStrings]
	)

	const headerMatchedOption = useMemo<IRateOption | null>(() => {
		if (!globalMealPlan) return null
		const option = roomTypeWiseRate.rate_options.find(
			(o) => o.meal_plan.ota_meal_plan.id === globalMealPlan.id
		)
		if (!option) return null
		return { id: option.meal_plan.id, name: option.name, rateOption: option }
	}, [globalMealPlan, roomTypeWiseRate.rate_options])

	const rateOptions = useMemo(
		() => getRatePlanOptions(roomTypeWiseRate),
		[roomTypeWiseRate]
	)

	const displayRatePlan = selectedRatePlan || headerMatchedOption

	const optionAvailability = useMemo(() => {
		if (!displayRatePlan) return null
		return getMinAvailableRoomsForOption(
			displayRatePlan.rateOption,
			dateStrings
		)
	}, [displayRatePlan, dateStrings])

	const soldOutDates = useMemo(() => {
		if (!displayRatePlan) return []
		return dateStrings.filter(
			(date) =>
				(displayRatePlan.rateOption.dates.find((d) => d.date_local === date)
					?.available_rooms ?? 0) <= 0
		)
	}, [displayRatePlan, dateStrings])
	const isFullySoldOut =
		soldOutDates.length > 0 && soldOutDates.length === dateStrings.length

	const avgBaseRate = useMemo(() => {
		if (!displayRatePlan) return undefined
		return getAvgBaseRate(displayRatePlan.rateOption)
	}, [displayRatePlan])

	const { value: discount } =
		useFieldValue<IHotelAvailabilityFormSchema["discount"]>("discount")

	const { freeChildAges } = usePaxPlacement()

	const maxRooms = displayRatePlan ? optionAvailability || 0 : maxOverallRooms

	function changeQty(newQty: number) {
		const otaRoomType = roomTypeWiseRate.room_type.ota_room_type
		const awebFieldName = `aweb_${id}` as const
		const cwebFieldName = `cweb_${id}` as const
		const cnbFieldName = `cnb_${id}` as const
		const complimentaryFieldName = `complimentary_${id}` as const
		const state = form.getState().values
		const currentAweb = state[awebFieldName] || 0
		const currentCweb = state[cwebFieldName] || []
		const currentCnb = state[cnbFieldName] || []
		const currentComplimentary = state[complimentaryFieldName] || []

		const isFreshAdd = qty === 0 && newQty > 0 && !state[`ppr_${id}`]
		const prefilledComplimentary = isFreshAdd
			? pickFreeChildSlots({
					options: childSlotOptions?.COMP || [],
					excludeIds: [
						...currentCweb,
						...currentCnb,
						...currentComplimentary,
					].map((s) => s.id),
					freeChildAges,
					prefer: "youngest",
				})
			: []
		const cnbLimit =
			roomTypeWiseRate.room_type.allowed_children_without_extra_bed
		const {
			aweb,
			cweb,
			cnb,
			complimentary: newComplimentary,
		} = clampExtras({
			ppr: isFreshAdd ? otaRoomType.base_adult_occupancy : ppr || 1,
			aweb: currentAweb,
			cweb: currentCweb,
			cnb: currentCnb,
			complimentary: prefilledComplimentary.length
				? [...currentComplimentary, ...prefilledComplimentary]
				: currentComplimentary,
			qty: newQty,
			maxOccupancy: otaRoomType.max_occupancy,
			maxAdultOccupancy: otaRoomType.max_adult_occupancy,
			maxChildOccupancy: otaRoomType.max_child_occupancy,
			allowedExtraBeds: roomTypeWiseRate.room_type.allowed_extra_beds,
			allowedAwebPerRoom:
				roomTypeWiseRate.room_type.allowed_adults_with_extra_bed,
			allowedCwebPerRoom:
				roomTypeWiseRate.room_type.allowed_children_with_extra_bed,
			allowedCnbPerRoom: typeof cnbLimit === "number" ? cnbLimit : null,
		})
		form.batch(() => {
			form.change(fieldName, newQty)
			if (!selectedRatePlan && newQty > 0 && headerMatchedOption) {
				form.change(rpFieldName, headerMatchedOption)
			}
			if (aweb !== currentAweb) form.change(awebFieldName, aweb)
			if (cweb !== currentCweb) form.change(cwebFieldName, cweb)
			if (cnb !== currentCnb) form.change(cnbFieldName, cnb)
			if (newComplimentary !== currentComplimentary)
				form.change(complimentaryFieldName, newComplimentary)
		})
	}
	return (
		<>
			<tr>
				<TableDataCell>
					<Inline gap="3" alignItems="start">
						<img
							src={roomTypeWiseRate.room_type.ota_room_type.thumb_image_url}
							alt={roomTypeWiseRate.room_type.name}
							style={{
								width: "72px",
								height: "72px",
								objectFit: "cover",
								borderRadius: "4px",
								flexShrink: 0,
							}}
						/>
						<Stack gap="px">
							<Text fontWeight="semibold">
								{roomTypeWiseRate.room_type.name}
							</Text>
							<Text fontSize="sm" color="muted">
								{pluralize(
									"Rate Plan",
									roomTypeWiseRate.rate_options.length,
									true
								)}{" "}
								| Max{" "}
								{pluralize(
									"Guest",
									roomTypeWiseRate.room_type.ota_room_type.max_occupancy,
									true
								)}
							</Text>
						</Stack>
					</Inline>
				</TableDataCell>
				<TableDataCell>
					<Stack gap="2" alignItems="start">
						<Inline alignItems="center">
							{optionAvailability === null ? (
								<Badge>—</Badge>
							) : isFullySoldOut ? (
								<Badge status="danger">Sold out</Badge>
							) : optionAvailability <= 0 ? (
								<Badge status="warning">Unavailable</Badge>
							) : (
								<Badge status="success">upto {optionAvailability} rooms</Badge>
							)}
							{displayRatePlan ? (
								<RateOptionAvailabilityTooltip
									rateOption={displayRatePlan.rateOption}
									dateStrings={dateStrings}
									ppr={qty > 0 ? ppr || 1 : 0}
								/>
							) : null}
						</Inline>
						{soldOutDates.length && !isFullySoldOut ? (
							<Text fontSize="xs" color="warning">
								Sold out on {formatNightDates(soldOutDates)}
							</Text>
						) : null}
					</Stack>
				</TableDataCell>
				<TableDataCell>
					{avgBaseRate ? (
						<Stack gap="1" alignItems="start">
							<Text fontSize="lg" fontWeight="semibold">
								{discount ? (
									<Text
										as="span"
										fontSize="sm"
										fontWeight="normal"
										color="muted"
										style={{ textDecoration: "line-through" }}
									>
										<Money money={avgBaseRate} />{" "}
									</Text>
								) : null}
								<Money
									money={
										discount
											? multipleMoneyBy(avgBaseRate, (100 - discount) / 100)
											: avgBaseRate
									}
									showCurrency
								/>
								<Text as="span" fontWeight="normal" fontSize="xs">
									{" "}
									+ Tax
								</Text>
							</Text>
							{discount ? (
								<Badge status="success" size="sm">
									{discount}% Discount
								</Badge>
							) : null}
						</Stack>
					) : (
						<Text fontSize="sm" color="muted">
							—
						</Text>
					)}
				</TableDataCell>
				<TableDataCell>
					<Inline width={"full"} justifyContent={"end"}>
						{qty > 0 ? (
							<Inline alignItems={"center"} gap="1">
								<Text fontSize={"sm"} fontWeight="semibold">
									Rooms
								</Text>
								<QuantityControl
									value={qty}
									min={0}
									max={maxRooms}
									incrementTooltip={
										qty >= maxRooms
											? maxRooms
												? `Only ${pluralize("room", maxRooms, true)} left`
												: "No rooms left"
											: undefined
									}
									onChange={changeQty}
								/>
							</Inline>
						) : (
							<Tooltip content={maxRooms ? undefined : "No rooms left"}>
								<Button
									level="secondary"
									disabled={!maxRooms}
									onClick={() => changeQty(1)}
								>
									<Icons.Plus /> Add Room
								</Button>
							</Tooltip>
						)}
					</Inline>
				</TableDataCell>
			</tr>

			{qty > 0 ? (
				<tr>
					<TableDataCell colSpan={4} style={{ maxWidth: 0 }}>
						<Stack gap="2">
							<Box bgColor="subtle" rounded="md" paddingX="4" paddingY="3">
								<Grid noGutters alignItems="start">
									<Col xs={12} lg={4} paddingX="2" paddingY="2">
										<OccupancyCell label="Select Rate Plan" alignLeft>
											<SelectField
												select={Select}
												name={rpFieldName}
												options={rateOptions}
												placeholder="Select a rate option"
												optionRenderer={({
													option,
												}: {
													option: IRateOption
												}) => {
													const avgRate = getAvgBaseRate(option.rateOption)
													return (
														<Inline
															justifyContent="between"
															alignItems="center"
															gap="4"
															width="full"
														>
															<Stack gap="px">
																<Text>{option.name}</Text>
																{option.rateOption.disabled &&
																option.rateOption.unavailable_reason ? (
																	<Text fontSize="xs" color="danger">
																		{option.rateOption.unavailable_reason}
																	</Text>
																) : null}
															</Stack>
															{avgRate ? (
																<Text fontSize="sm" color="muted">
																	<Money money={avgRate} />{" "}
																	<Text as="span" fontSize="xs">
																		+ Tax
																	</Text>
																</Text>
															) : null}
														</Inline>
													)
												}}
											/>
										</OccupancyCell>
									</Col>
									{selectedRatePlan ? (
										<Col xs={12} lg={8}>
											<Grid noGutters alignItems="start">
												<Col xs={6} sm={4} md paddingX="2" paddingY="2">
													<OccupancyCell label="Base Pax/Room">
														<PersonsPerRoomField
															id={id}
															qty={qty}
															roomType={roomTypeWiseRate.room_type}
														/>
														<BaseChargeLine
															qty={qty}
															ppr={ppr || 1}
															dateStrings={dateStrings}
															selectedRatePlan={selectedRatePlan}
															roomType={roomTypeWiseRate.room_type}
														/>
													</OccupancyCell>
												</Col>
												<Col xs={6} sm={4} md paddingX="2" paddingY="2">
													<OccupancyCell label="Total AWEB">
														<AwebField
															id={id}
															qty={qty}
															ppr={ppr || 1}
															roomType={roomTypeWiseRate.room_type}
														/>
														<AwebChargeLine
															id={id}
															dateStrings={dateStrings}
															selectedRatePlan={selectedRatePlan}
														/>
													</OccupancyCell>
												</Col>
												<Col xs={6} sm={4} md paddingX="2" paddingY="2">
													<OccupancyCell label="Total CWEB">
														<CwebField
															id={id}
															qty={qty}
															ppr={ppr || 1}
															options={childSlotOptions?.CWEB || []}
															roomType={roomTypeWiseRate.room_type}
															reclassifiedChildSlotIds={
																reclassifiedChildSlotIds
															}
														/>
														<ChildChargeLine
															id={id}
															bucket="CWEB"
															dateStrings={dateStrings}
															selectedRatePlan={selectedRatePlan}
														/>
													</OccupancyCell>
												</Col>
												<Col xs={6} sm={4} md paddingX="2" paddingY="2">
													<OccupancyCell label="Total CNB">
														<CnbField
															id={id}
															qty={qty}
															ppr={ppr || 1}
															options={childSlotOptions?.CNB || []}
															roomType={roomTypeWiseRate.room_type}
															reclassifiedChildSlotIds={
																reclassifiedChildSlotIds
															}
														/>
														<ChildChargeLine
															id={id}
															bucket="CNB"
															dateStrings={dateStrings}
															selectedRatePlan={selectedRatePlan}
														/>
													</OccupancyCell>
												</Col>
												<Col xs={6} sm={4} md paddingX="2" paddingY="2">
													<OccupancyCell label="Total Comp Child">
														<ComplimentaryField
															id={id}
															qty={qty}
															ppr={ppr || 1}
															options={childSlotOptions?.COMP || []}
															roomType={roomTypeWiseRate.room_type}
															reclassifiedChildSlotIds={
																reclassifiedChildSlotIds
															}
														/>
													</OccupancyCell>
												</Col>
											</Grid>
										</Col>
									) : null}
								</Grid>
								{selectedRatePlan ? (
									<Inline
										justifyContent="end"
										borderTopWidth="1"
										marginTop="3"
										paddingTop="3"
									>
										<Box textAlign="right">
											<RoomGroupPrice
												id={id}
												qty={qty}
												ppr={ppr || 1}
												dateStrings={dateStrings}
												selectedRatePlan={selectedRatePlan}
												roomType={roomTypeWiseRate.room_type}
											/>
										</Box>
									</Inline>
								) : null}
							</Box>
							{selectedRatePlan ? (
								<RoomAllocationTable
									id={id}
									qty={qty}
									ppr={ppr || 1}
									dateStrings={dateStrings}
									selectedRatePlan={selectedRatePlan}
									roomType={roomTypeWiseRate.room_type}
								/>
							) : null}
						</Stack>
					</TableDataCell>
				</tr>
			) : null}
		</>
	)
}

export interface IRateOption {
	id: number
	name: string
	rateOption: IAvailabilityRateOption
}

function getRatePlanOptions(
	roomTypeWiseRate: IAvailabilityRoomWiseRateOptions
): Array<IRateOption> {
	return roomTypeWiseRate.rate_options.map((option) => ({
		id: option.meal_plan.id,
		name: option.name,
		rateOption: option,
		disabled: option.disabled,
	}))
}

function OccupancyCell({
	label,
	alignLeft,
	children,
}: {
	label: string
	alignLeft?: boolean
	children: React.ReactNode
}) {
	return (
		<Stack gap="2" alignItems={alignLeft ? "stretch" : "center"}>
			<Text
				fontSize="sm"
				fontWeight="semibold"
				textAlign={alignLeft ? "left" : "center"}
			>
				{label}
			</Text>
			{children}
		</Stack>
	)
}

/**
 * Base charge per night for the group, e.g. "2 × 4,950 = 9,900" (before tax).
 */
function BaseChargeLine({
	qty,
	ppr,
	dateStrings,
	selectedRatePlan,
	roomType,
}: {
	qty: number
	ppr: number
	dateStrings: Array<string>
	selectedRatePlan: IRateOption
	roomType: IHotelRemoteRoomType
}) {
	const { value: discount } =
		useFieldValue<IHotelAvailabilityFormSchema["discount"]>("discount")
	const avgCharge = useMemo(() => {
		const nightly = getNightlyPricesForRoomGroup(
			selectedRatePlan.rateOption,
			roomType,
			dateStrings,
			{
				qty,
				ppr,
				aweb: 0,
				cwebAges: [],
				cnbAges: [],
				complimentaryAges: [],
				discountPercent: discount || 0,
			}
		)
		const charges = (nightly || []).flatMap((n) =>
			n.rooms.map((r) =>
				r.breakdown.pricing_type === "RBP"
					? r.breakdown.base.amount
					: r.breakdown.adult_amount
			)
		)
		const [first, ...rest] = charges
		return first
			? divideMoneyBy(addMoney(first, ...rest), charges.length)
			: undefined
	}, [selectedRatePlan, roomType, dateStrings, qty, ppr, discount])
	if (!avgCharge || !ppr) return null
	return (
		<Stack gap="px" alignItems="center">
			<Text fontSize="xs" color="muted">
				{qty} &times; <Money money={avgCharge} />
			</Text>
			<Text fontSize="xs" color="muted">
				= <Money money={multipleMoneyBy(avgCharge, qty)} />
			</Text>
		</Stack>
	)
}

/**
 * AWEB charge per night, e.g. "2 × 1,500 = 3,000" (before tax).
 */
function AwebChargeLine({
	id,
	dateStrings,
	selectedRatePlan,
}: {
	id: number
	dateStrings: Array<string>
	selectedRatePlan: IRateOption
}) {
	const { value: aweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`aweb_${number}`]
	>(`aweb_${id}` as const)
	const count = aweb || 0
	if (!count) return null
	const rate = getExtraNightlyRate(
		selectedRatePlan.rateOption,
		dateStrings,
		"AWEB"
	)
	if (!rate) {
		return (
			<Text fontSize="xs" color="muted">
				—
			</Text>
		)
	}
	return (
		<Stack gap="px" alignItems="center">
			<Text fontSize="xs" color="muted">
				{count} &times; {rate.varies ? "avg " : ""}
				<Money money={rate.avg} />
			</Text>
			<Text fontSize="xs" color="muted">
				= <Money money={multipleMoneyBy(rate.avg, count)} />
			</Text>
		</Stack>
	)
}

/**
 * CWEB/CNB charge per night; rates can differ by age band, so only the total
 * is shown with a per-child breakdown on hover.
 */
function ChildChargeLine({
	id,
	bucket,
	dateStrings,
	selectedRatePlan,
}: {
	id: number
	bucket: "CWEB" | "CNB"
	dateStrings: Array<string>
	selectedRatePlan: IRateOption
}) {
	const { value: slots } = useFieldValue<
		IHotelAvailabilityFormSchema[`cweb_${number}`]
	>(getChildSlotFieldName(bucket, id))
	const children = (slots || []).map((slot) => ({
		slot,
		rate: getExtraNightlyRate(
			selectedRatePlan.rateOption,
			dateStrings,
			bucket,
			slot.age
		),
	}))
	if (!children.length) return null
	const rates = children.flatMap((c) => (c.rate ? [c.rate.avg] : []))
	const [first, ...rest] = rates
	if (!first || rates.length !== children.length) {
		return (
			<Text fontSize="xs" color="muted">
				—
			</Text>
		)
	}
	const total = addMoney(first, ...rest)
	return (
		<Inline gap="px" alignItems="center">
			<Text fontSize="xs" color="muted">
				= <Money money={total} />
			</Text>
			<Tooltip
				interactive
				content={
					<Stack gap="2">
						<Text fontSize="sm" fontWeight="semibold">
							{CHILD_SLOT_BUCKETS[bucket].label} Rates
						</Text>
						<Stack gap="1">
							{children.map(({ slot, rate }) => (
								<Inline key={slot.id} gap="4" alignItems="center">
									<Text fontSize="sm" style={{ width: "32px" }}>
										{slot.age}y
									</Text>
									<Box flex="1">
										<Text fontSize="xs" color="muted">
											{rate?.occupancy}
										</Text>
									</Box>
									<Text
										fontSize="sm"
										fontWeight="semibold"
										style={{ textAlign: "right" }}
									>
										{rate?.varies ? (
											<Text as="span" fontSize="xs" fontWeight="normal">
												avg{" "}
											</Text>
										) : null}
										{rate ? <Money money={rate.avg} /> : "—"}
									</Text>
								</Inline>
							))}
							<Inline
								gap="4"
								alignItems="center"
								borderTopWidth="1"
								paddingTop="1"
							>
								<Box flex="1">
									<Text fontSize="sm" fontWeight="semibold">
										Total
									</Text>
								</Box>
								<Text fontSize="sm" fontWeight="semibold">
									<Money money={total} />
								</Text>
							</Inline>
						</Stack>
					</Stack>
				}
			>
				<Box
					as="span"
					display="inlineFlex"
					color="accent"
					marginLeft="1"
					style={{ cursor: "help", verticalAlign: "middle" }}
				>
					<Icons.Info size="3" />
				</Box>
			</Tooltip>
		</Inline>
	)
}

function clampExtras({
	ppr,
	aweb,
	cweb,
	cnb,
	complimentary,
	qty,
	maxOccupancy,
	maxAdultOccupancy,
	maxChildOccupancy,
	allowedExtraBeds,
	allowedAwebPerRoom,
	allowedCwebPerRoom,
	allowedCnbPerRoom,
}: {
	ppr: number
	aweb: number
	cweb: Array<IChildSlotOption>
	cnb: Array<IChildSlotOption>
	complimentary: Array<IChildSlotOption>
	qty: number
	maxOccupancy: number
	maxAdultOccupancy: number
	maxChildOccupancy: number
	allowedExtraBeds: number
	allowedAwebPerRoom: number
	allowedCwebPerRoom: number
	allowedCnbPerRoom: number | null // null = no per-type limit
}): {
	aweb: number
	cweb: Array<IChildSlotOption>
	cnb: Array<IChildSlotOption>
	complimentary: Array<IChildSlotOption>
} {
	const rooms = Math.max(qty, 1)
	const occupancyBudgetRaw = Math.max(0, (maxOccupancy - ppr) * rooms)
	const childBudgetRaw = Math.max(0, maxChildOccupancy * rooms)
	// Complimentary is normally protected — AWEB/CWEB/CNB shrink to leave room
	// for it, not the other way round. But if the budget itself has shrunk
	// below the complimentary count (e.g. qty went down), there's no way
	// around it — complimentary must shrink too.
	const complimentaryLimit = Math.min(occupancyBudgetRaw, childBudgetRaw)
	const clampedComplimentary =
		complimentary.length > complimentaryLimit
			? complimentary.slice(0, complimentaryLimit)
			: complimentary
	const occupancyBudget = Math.max(
		0,
		occupancyBudgetRaw - clampedComplimentary.length
	)
	const childBudget = Math.max(0, childBudgetRaw - clampedComplimentary.length)
	const extraBedsBudget = allowedExtraBeds * rooms
	const adultOccupancyBudget = Math.max(0, (maxAdultOccupancy - ppr) * rooms)

	const clampedAweb = Math.min(
		aweb,
		Math.max(
			0,
			Math.min(
				allowedAwebPerRoom * rooms,
				extraBedsBudget,
				occupancyBudget,
				adultOccupancyBudget
			)
		)
	)

	const cwebMax = Math.max(
		0,
		Math.min(
			allowedCwebPerRoom * rooms,
			extraBedsBudget - clampedAweb,
			occupancyBudget - clampedAweb,
			childBudget
		)
	)
	const clampedCweb = cweb.length > cwebMax ? cweb.slice(0, cwebMax) : cweb

	const cnbLimit =
		allowedCnbPerRoom !== null ? allowedCnbPerRoom * rooms : occupancyBudget
	const cnbMax = Math.max(
		0,
		Math.min(
			cnbLimit,
			occupancyBudget - clampedAweb - clampedCweb.length,
			childBudget - clampedCweb.length
		)
	)
	const clampedCnb = cnb.length > cnbMax ? cnb.slice(0, cnbMax) : cnb

	return {
		aweb: clampedAweb,
		cweb: clampedCweb,
		cnb: clampedCnb,
		complimentary: clampedComplimentary,
	}
}

function getRoomAllocationLabel(
	ppr: number,
	breakdown: TRoomNightPriceBreakdown
): string {
	if (breakdown.pricing_type !== "RBP") {
		return [
			`Adults: ${breakdown.adult_tier}`,
			breakdown.child_tier ? `Children: ${breakdown.child_tier}` : null,
		]
			.filter(Boolean)
			.join(" · ")
	}
	const parts: Array<string> = [`${ppr} ${pluralize("Adult", ppr)}`]
	if (breakdown.aweb) parts.push(`${breakdown.aweb.count} AWEB`)
	if (breakdown.cweb) {
		parts.push(
			`CWEB (${breakdown.cweb.children.map((c) => `${c.age}y`).join(", ")})`
		)
	}
	if (breakdown.cnb) {
		parts.push(
			`CNB (${breakdown.cnb.children.map((c) => `${c.age}y`).join(", ")})`
		)
	}
	if (breakdown.complimentary) {
		parts.push(
			`Comp (${breakdown.complimentary.children.map((c) => `${c.age}y`).join(", ")})`
		)
	}
	return parts.join(" · ")
}

function getRoomBreakdownTooltipContent(
	breakdown: TRoomNightPriceBreakdown
): React.ReactNode {
	if (breakdown.pricing_type !== "RBP") {
		return (
			<Stack gap="1">
				<Inline justifyContent="between" gap="4">
					<Text fontSize="sm">Adults ({breakdown.adult_tier})</Text>
					<Text fontSize="sm">
						<Money money={breakdown.adult_amount} />
					</Text>
				</Inline>
				{breakdown.child_tier && breakdown.child_amount !== undefined ? (
					<Inline justifyContent="between" gap="4">
						<Text fontSize="sm">Children ({breakdown.child_tier})</Text>
						<Text fontSize="sm">
							<Money money={breakdown.child_amount} />
						</Text>
					</Inline>
				) : null}
			</Stack>
		)
	}

	return (
		<Stack gap="1">
			<Inline justifyContent="between" gap="4">
				<Text fontSize="sm">Base ({breakdown.base.tier})</Text>
				<Text fontSize="sm">
					<Money money={breakdown.base.amount} />
				</Text>
			</Inline>
			{breakdown.aweb ? (
				<Inline justifyContent="between" gap="4">
					<Text fontSize="sm">AWEB ({breakdown.aweb.count})</Text>
					<Text fontSize="sm">
						<Money money={breakdown.aweb.amount} />
					</Text>
				</Inline>
			) : null}
			{breakdown.cweb?.children.map((c, i) => (
				<Inline key={`cweb-${i}`} justifyContent="between" gap="4">
					<Text fontSize="sm">CWEB ({c.age}y)</Text>
					<Text fontSize="sm">
						<Money money={c.rate} />
					</Text>
				</Inline>
			))}
			{breakdown.cnb?.children.map((c, i) => (
				<Inline key={`cnb-${i}`} justifyContent="between" gap="4">
					<Text fontSize="sm">CNB ({c.age}y)</Text>
					<Text fontSize="sm">
						<Money money={c.rate} />
					</Text>
				</Inline>
			))}
			{breakdown.complimentary?.children.map((c, i) => (
				<Inline key={`comp-${i}`} justifyContent="between" gap="4">
					<Text fontSize="sm">Complimentary ({c.age}y)</Text>
					<Text fontSize="sm">Free</Text>
				</Inline>
			))}
			{breakdown.tax ? (
				<Inline
					justifyContent="between"
					gap="4"
					borderTopWidth="1"
					paddingTop="1"
				>
					<Text fontSize="sm">Tax ({breakdown.tax.rate}%)</Text>
					<Text fontSize="sm">
						+<Money money={breakdown.tax.amount} />
					</Text>
				</Inline>
			) : null}
		</Stack>
	)
}

function RoomAllocationTable({
	id,
	qty,
	ppr,
	dateStrings,
	selectedRatePlan,
	roomType,
}: {
	id: number
	qty: number
	ppr: number
	dateStrings: Array<string>
	selectedRatePlan: IRateOption
	roomType: IHotelRemoteRoomType
}) {
	const [expanded, setExpanded] = useState(false)
	const { value: aweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`aweb_${number}`]
	>(`aweb_${id}` as const)
	const { value: cweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cweb_${number}`]
	>(`cweb_${id}` as const)
	const { value: cnb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cnb_${number}`]
	>(`cnb_${id}` as const)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(`complimentary_${id}` as const)
	const { value: discount } =
		useFieldValue<IHotelAvailabilityFormSchema["discount"]>("discount")

	const nightly = useMemo(
		() =>
			getNightlyPricesForRoomGroup(
				selectedRatePlan.rateOption,
				roomType,
				dateStrings,
				{
					qty,
					ppr,
					aweb: aweb || 0,
					cwebAges: (cweb || []).map((s) => s.age),
					cnbAges: (cnb || []).map((s) => s.age),
					complimentaryAges: (complimentary || []).map((s) => s.age),
					discountPercent: discount || 0,
				}
			),
		[
			selectedRatePlan,
			roomType,
			dateStrings,
			qty,
			ppr,
			aweb,
			cweb,
			cnb,
			complimentary,
			discount,
		]
	)

	if (!nightly?.length) return null

	// Allocation (who's in which room) is identical every night for a given
	// selection — same occupancy inputs feed distributeExtrasAcrossRooms each
	// time — so the header only needs the first night's breakdown.
	const roomLabels = nightly[0].rooms.map((room) =>
		getRoomAllocationLabel(ppr, room.breakdown)
	)
	const currency = nightly[0].rooms[0]?.total.currency

	const taxSavingsEntries = nightly
		.map((n) => n.tax_savings)
		.filter((s): s is TMoney => Boolean(s))
	const totalTaxSavings = taxSavingsEntries.length
		? addMoney(taxSavingsEntries[0], ...taxSavingsEntries.slice(1))
		: undefined
	const hasTaxSavings =
		totalTaxSavings && Number(formatMoneyByDecimal(totalTaxSavings)) > 0

	return (
		<Box>
			<Stack gap="2">
				<Inline justifyContent="center">
					<Button
						inline
						size="sm"
						level="tertiary"
						onClick={() => setExpanded((v) => !v)}
					>
						<Inline gap="2" alignItems="center">
							<Text fontSize="sm" fontWeight="semibold" color="accent">
								{expanded ? "Hide" : "Show"} Night-Wise Room Allocation
							</Text>
							<Text fontSize="xs" color="muted">
								(prices inc. Tax)
							</Text>
							<Icons.ChevronDown
								style={{
									transform: expanded ? "rotate(180deg)" : undefined,
								}}
							/>
						</Inline>
					</Button>
				</Inline>
				{hasTaxSavings && totalTaxSavings ? (
					<Alert status="success">
						Smart allocation saved{" "}
						<Text as="span" fontWeight="semibold">
							<Money money={totalTaxSavings} showCurrency />
						</Text>{" "}
						in tax on this stay.
					</Alert>
				) : null}
				{expanded ? (
					<Box style={{ overflowX: "auto", width: "100%" }}>
						<Table bordered responsive>
							<thead>
								<tr>
									<TableHeaderDataCell bgColor="subtle">
										<Text fontSize="sm" fontWeight="semibold" color="muted">
											Night
										</Text>
									</TableHeaderDataCell>
									{roomLabels.map((label, i) => (
										<TableHeaderDataCell key={i} bgColor="subtle">
											<Stack gap="px">
												<Text fontWeight="semibold" fontSize="sm">
													Room {i + 1}
												</Text>
												<Text fontSize="xs" color="muted">
													{label}
												</Text>
											</Stack>
										</TableHeaderDataCell>
									))}
								</tr>
							</thead>
							<tbody>
								{nightly.map((night) => (
									<tr key={night.date}>
										<TableDataCell>
											<Text fontSize="sm" color="muted">
												{formatDate(night.date, "DD MMM")}
											</Text>
										</TableDataCell>
										{night.rooms.map((room) => (
											<TableDataCell key={room.room_index}>
												<Tooltip
													content={getRoomBreakdownTooltipContent(
														room.breakdown
													)}
												>
													<Text
														as="span"
														style={{
															cursor: "help",
															textDecoration: "underline dotted",
														}}
													>
														<Money money={room.total} title={undefined} />
													</Text>
												</Tooltip>
											</TableDataCell>
										))}
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr>
									<TableFooterDataCell>
										<Text fontSize="sm" fontWeight="semibold">
											Subtotal {currency ? `(${currency})` : ""}
										</Text>
									</TableFooterDataCell>
									{nightly[0].rooms.map((_, roomIndex) => {
										const roomTotal = addMoney(
											nightly[0].rooms[roomIndex].total,
											...nightly.slice(1).map((n) => n.rooms[roomIndex].total)
										)
										return (
											<TableFooterDataCell key={roomIndex}>
												<Money money={roomTotal} />
											</TableFooterDataCell>
										)
									})}
								</tr>
							</tfoot>
						</Table>
					</Box>
				) : null}
			</Stack>
		</Box>
	)
}

function PersonsPerRoomField({
	id,
	qty,
	roomType,
}: {
	id: number
	qty: number
	roomType: IHotelRemoteRoomType
}) {
	const otaRoomType = roomType.ota_room_type
	const form = useForm<IHotelAvailabilityFormSchema>()
	const pprFieldName = `ppr_${id}` as const
	const awebFieldName = `aweb_${id}` as const
	const cwebFieldName = `cweb_${id}` as const
	const cnbFieldName = `cnb_${id}` as const
	const complimentaryFieldName = `complimentary_${id}` as const
	const { value: ppr } =
		useFieldValue<IHotelAvailabilityFormSchema[`ppr_${number}`]>(pprFieldName)
	const { value: aweb } =
		useFieldValue<IHotelAvailabilityFormSchema[`aweb_${number}`]>(awebFieldName)
	const { value: cweb } =
		useFieldValue<IHotelAvailabilityFormSchema[`cweb_${number}`]>(cwebFieldName)
	const { value: cnb } =
		useFieldValue<IHotelAvailabilityFormSchema[`cnb_${number}`]>(cnbFieldName)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(complimentaryFieldName)
	const hasExtras = Boolean(
		aweb ||
		(cweb || []).length ||
		(cnb || []).length ||
		(complimentary || []).length
	)

	const maxPpr = useMemo(() => {
		return Math.min(
			otaRoomType.base_adult_occupancy,
			otaRoomType.max_adult_occupancy
		)
	}, [otaRoomType])

	const { statuses } = usePaxPlacement()

	// Occupancy constraints live on the room type, not the rate option, so
	// switching rate options can't change them — this only needs to run once,
	// on mount, to seed a sensible ppr the first time a room type is picked.
	const prevRoomTypeIdRef = useRef<number | undefined>(undefined)
	useEffect(() => {
		if (otaRoomType.id === prevRoomTypeIdRef.current) return
		prevRoomTypeIdRef.current = otaRoomType.id

		const state = form.getState().values
		const currentPpr = state[pprFieldName] || 0
		const newPpr =
			currentPpr === 0
				? otaRoomType.base_adult_occupancy
				: Math.min(currentPpr, maxPpr)

		const currentCweb = state[cwebFieldName] || []
		const currentCnb = state[cnbFieldName] || []
		const currentAweb = state[awebFieldName] || 0
		const currentComplimentary = state[complimentaryFieldName] || []
		const cnbLimit = roomType.allowed_children_without_extra_bed
		const {
			aweb,
			cweb,
			cnb,
			complimentary: newComplimentary,
		} = clampExtras({
			ppr: newPpr,
			aweb: currentAweb,
			cweb: currentCweb,
			cnb: currentCnb,
			complimentary: currentComplimentary,
			qty,
			maxOccupancy: otaRoomType.max_occupancy,
			maxAdultOccupancy: otaRoomType.max_adult_occupancy,
			maxChildOccupancy: otaRoomType.max_child_occupancy,
			allowedExtraBeds: roomType.allowed_extra_beds,
			allowedAwebPerRoom: roomType.allowed_adults_with_extra_bed,
			allowedCwebPerRoom: roomType.allowed_children_with_extra_bed,
			allowedCnbPerRoom: typeof cnbLimit === "number" ? cnbLimit : null,
		})

		form.batch(() => {
			if (newPpr !== currentPpr) form.change(pprFieldName, newPpr)
			if (aweb !== currentAweb) form.change(awebFieldName, aweb)
			if (cweb !== currentCweb) form.change(cwebFieldName, cweb)
			if (cnb !== currentCnb) form.change(cnbFieldName, cnb)
			if (newComplimentary !== currentComplimentary)
				form.change(complimentaryFieldName, newComplimentary)
		})
	}, [
		otaRoomType,
		roomType,
		qty,
		form,
		pprFieldName,
		awebFieldName,
		cwebFieldName,
		cnbFieldName,
		complimentaryFieldName,
		maxPpr,
	])

	const worstNight = getWorstNightPaxStatus(statuses)
	const warning = useEmptySpareBedWarning("base", id)
	const spareBedChildAges =
		getRoomTypeSpareBeds(worstNight, id)?.base.childAges || []

	return (
		<Stack gap="1" alignItems="center">
			<QuantityControl
				value={ppr || 1}
				min={0}
				max={maxPpr}
				incrementTooltip={
					(ppr || 1) >= maxPpr
						? `Max ${maxPpr} ${pluralize("adult", maxPpr)} per room`
						: undefined
				}
				disableDecrement={hasExtras}
				decrementTooltip={
					hasExtras
						? "Remove all extra adults/children before reducing Base Pax/Room"
						: undefined
				}
				onChange={(v) => {
					const state = form.getState().values
					const currentCweb = state[cwebFieldName] || []
					const currentCnb = state[cnbFieldName] || []
					const currentAweb = state[awebFieldName] || 0
					const currentComplimentary = state[complimentaryFieldName] || []
					const cnbLimit = roomType.allowed_children_without_extra_bed
					const {
						aweb,
						cweb,
						cnb,
						complimentary: newComplimentary,
					} = clampExtras({
						ppr: v,
						aweb: currentAweb,
						cweb: currentCweb,
						cnb: currentCnb,
						complimentary: currentComplimentary,
						qty,
						maxOccupancy: otaRoomType.max_occupancy,
						maxAdultOccupancy: otaRoomType.max_adult_occupancy,
						maxChildOccupancy: otaRoomType.max_child_occupancy,
						allowedExtraBeds: roomType.allowed_extra_beds,
						allowedAwebPerRoom: roomType.allowed_adults_with_extra_bed,
						allowedCwebPerRoom: roomType.allowed_children_with_extra_bed,
						allowedCnbPerRoom: typeof cnbLimit === "number" ? cnbLimit : null,
					})
					form.batch(() => {
						form.change(pprFieldName, v)
						if (aweb !== currentAweb) form.change(awebFieldName, aweb)
						if (cweb !== currentCweb) form.change(cwebFieldName, cweb)
						if (cnb !== currentCnb) form.change(cnbFieldName, cnb)
						if (newComplimentary !== currentComplimentary)
							form.change(complimentaryFieldName, newComplimentary)
					})
				}}
			/>
			<StepperHints
				warning={warning}
				info={[getSpareBedMessage(spareBedChildAges)]}
			/>
		</Stack>
	)
}

// Several independent caps feed into one Math.min() for `max` — this picks
// out whichever cap is actually binding so the tooltip names the real reason
// (e.g. "extra bed limit" vs the generic room occupancy budget) instead of
// always blaming occupancy.
function getCapacityLimitMessage(
	terms: Array<{ value: number; message: string }>
): string {
	const min = Math.min(...terms.map((t) => t.value))
	return (
		terms.find((t) => t.value === min)?.message ||
		"Room occupancy limit reached"
	)
}

function AwebField({
	id,
	qty,
	ppr,
	roomType,
}: {
	id: number
	qty: number
	ppr: number
	roomType: IHotelRemoteRoomType
}) {
	const otaRoomType = roomType.ota_room_type
	const form = useForm<IHotelAvailabilityFormSchema>()
	const awebFieldName = `aweb_${id}` as const
	const { value: aweb } =
		useFieldValue<IHotelAvailabilityFormSchema[`aweb_${number}`]>(awebFieldName)
	const { value: cweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cweb_${number}`]
	>(`cweb_${id}` as const)
	const { value: cnb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cnb_${number}`]
	>(`cnb_${id}` as const)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(`complimentary_${id}` as const)

	const occupancyBudget = Math.max(0, (otaRoomType.max_occupancy - ppr) * qty)

	const pprBelowBase = ppr < otaRoomType.base_adult_occupancy

	const limitTerms: Array<{ value: number; message: string }> = [
		{
			value: roomType.allowed_adults_with_extra_bed * qty,
			message: `Room allows only ${roomType.allowed_adults_with_extra_bed} ${pluralize("adult", roomType.allowed_adults_with_extra_bed)} with extra bed per room`,
		},
		{
			value: roomType.allowed_extra_beds * qty - (cweb || []).length,
			message: `Room's extra bed limit reached (shared with CWEB)`,
		},
		{
			value:
				occupancyBudget -
				(cweb || []).length -
				(cnb || []).length -
				(complimentary || []).length,
			message: "Room occupancy limit reached",
		},
		{
			value: Math.max(0, (otaRoomType.max_adult_occupancy - ppr) * qty),
			message: "Room's max adult occupancy limit reached",
		},
	]
	const max = Math.max(0, Math.min(...limitTerms.map((t) => t.value)))
	const limitMessage = getCapacityLimitMessage(limitTerms)
	const warning = useEmptySpareBedWarning("aweb", id, Boolean(aweb))
	const { statuses } = usePaxPlacement()
	const spareBedChildAges = aweb
		? getRoomTypeSpareBeds(getWorstNightPaxStatus(statuses), id)?.aweb
				.childAges || []
		: []

	return (
		<Stack gap="1" alignItems="center">
			<QuantityControl
				value={aweb || 0}
				min={0}
				max={max}
				onChange={(v) => form.change(awebFieldName, v)}
				disabled={!roomType.allowed_adults_with_extra_bed}
				disableIncrement={pprBelowBase}
				incrementTooltip={
					pprBelowBase
						? "Fill Base Pax first"
						: (aweb || 0) >= max
							? limitMessage
							: undefined
				}
			/>
			<StepperHints
				warning={warning}
				info={[getSpareBedMessage(spareBedChildAges)]}
			/>
		</Stack>
	)
}

function CwebField({
	id,
	qty,
	ppr,
	options,
	roomType,
	reclassifiedChildSlotIds,
}: {
	id: number
	qty: number
	ppr: number
	options: Array<IChildSlotOption>
	roomType: IHotelRemoteRoomType
	reclassifiedChildSlotIds?: Set<number>
}) {
	const otaRoomType = roomType.ota_room_type
	const cwebFieldName = `cweb_${id}` as const
	const { value: aweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`aweb_${number}`]
	>(`aweb_${id}` as const)
	const { value: cnb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cnb_${number}`]
	>(`cnb_${id}` as const)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(`complimentary_${id}` as const)
	const { value: cweb } =
		useFieldValue<IHotelAvailabilityFormSchema[`cweb_${number}`]>(cwebFieldName)

	const cnbSelected = useMemo(() => cnb || [], [cnb])
	const complimentarySelected = useMemo(
		() => complimentary || [],
		[complimentary]
	)
	const cwebSelected = useMemo(() => cweb || [], [cweb])

	const occupancyBudget = Math.max(0, (otaRoomType.max_occupancy - ppr) * qty)

	const pprBelowBase = ppr < otaRoomType.base_adult_occupancy

	const limitTerms: Array<{ value: number; message: string }> = [
		{
			value: roomType.allowed_children_with_extra_bed * qty,
			message: `Room allows only ${roomType.allowed_children_with_extra_bed} ${pluralize("child", roomType.allowed_children_with_extra_bed)} with extra bed per room`,
		},
		{
			value: roomType.allowed_extra_beds * qty - (aweb || 0),
			message: `Room's extra bed limit reached (shared with AWEB)`,
		},
		{
			value:
				occupancyBudget -
				(aweb || 0) -
				cnbSelected.length -
				complimentarySelected.length,
			message: "Room occupancy limit reached",
		},
		{
			value:
				otaRoomType.max_child_occupancy * qty -
				cnbSelected.length -
				complimentarySelected.length,
			message: "Room's max child occupancy limit reached",
		},
	]
	const max = Math.max(0, Math.min(...limitTerms.map((t) => t.value)))

	return (
		<ChildSlotStepper
			roomTypeId={id}
			bucket="CWEB"
			options={options}
			roomTypeSlots={{
				CWEB: cwebSelected,
				CNB: cnbSelected,
				COMP: complimentarySelected,
			}}
			max={max}
			disabled={!roomType.allowed_children_with_extra_bed}
			pprBelowBase={pprBelowBase}
			limitMessage={getCapacityLimitMessage(limitTerms)}
			reclassifiedChildSlotIds={reclassifiedChildSlotIds}
		/>
	)
}

function CnbField({
	id,
	qty,
	ppr,
	options,
	roomType,
	reclassifiedChildSlotIds,
}: {
	id: number
	qty: number
	ppr: number
	options: Array<IChildSlotOption>
	roomType: IHotelRemoteRoomType
	reclassifiedChildSlotIds?: Set<number>
}) {
	const otaRoomType = roomType.ota_room_type
	const cnbFieldName = `cnb_${id}` as const
	const { value: aweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`aweb_${number}`]
	>(`aweb_${id}` as const)
	const { value: cweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cweb_${number}`]
	>(`cweb_${id}` as const)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(`complimentary_${id}` as const)
	const { value: cnb } =
		useFieldValue<IHotelAvailabilityFormSchema[`cnb_${number}`]>(cnbFieldName)

	const cwebSelected = useMemo(() => cweb || [], [cweb])
	const complimentarySelected = useMemo(
		() => complimentary || [],
		[complimentary]
	)
	const cnbSelected = useMemo(() => cnb || [], [cnb])

	const occupancyBudget = Math.max(0, (otaRoomType.max_occupancy - ppr) * qty)
	const cnbLimit = roomType.allowed_children_without_extra_bed
	const hasCnbLimit = typeof cnbLimit === "number"

	const pprBelowBase = ppr < otaRoomType.base_adult_occupancy

	const limitTerms: Array<{ value: number; message: string }> = [
		{
			value: hasCnbLimit ? cnbLimit * qty : Infinity,
			message: `Room allows only ${cnbLimit} ${pluralize("child", cnbLimit)} without extra bed per room`,
		},
		{
			value:
				occupancyBudget -
				(aweb || 0) -
				cwebSelected.length -
				complimentarySelected.length,
			message: "Room occupancy limit reached",
		},
		{
			value:
				otaRoomType.max_child_occupancy * qty -
				cwebSelected.length -
				complimentarySelected.length,
			message: "Room's max child occupancy limit reached",
		},
	]
	const max = Math.max(0, Math.min(...limitTerms.map((t) => t.value)))

	return (
		<ChildSlotStepper
			roomTypeId={id}
			bucket="CNB"
			options={options}
			roomTypeSlots={{
				CWEB: cwebSelected,
				CNB: cnbSelected,
				COMP: complimentarySelected,
			}}
			max={max}
			disabled={hasCnbLimit && !cnbLimit}
			pprBelowBase={pprBelowBase}
			limitMessage={getCapacityLimitMessage(limitTerms)}
			reclassifiedChildSlotIds={reclassifiedChildSlotIds}
		/>
	)
}

function ComplimentaryField({
	id,
	qty,
	ppr,
	options,
	roomType,
	reclassifiedChildSlotIds,
}: {
	id: number
	qty: number
	ppr: number
	options: Array<IChildSlotOption>
	roomType: IHotelRemoteRoomType
	reclassifiedChildSlotIds?: Set<number>
}) {
	const otaRoomType = roomType.ota_room_type
	const complimentaryFieldName = `complimentary_${id}` as const
	const { value: aweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`aweb_${number}`]
	>(`aweb_${id}` as const)
	const { value: cweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cweb_${number}`]
	>(`cweb_${id}` as const)
	const { value: cnb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cnb_${number}`]
	>(`cnb_${id}` as const)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(complimentaryFieldName)

	const cwebSelected = useMemo(() => cweb || [], [cweb])
	const cnbSelected = useMemo(() => cnb || [], [cnb])
	const complimentarySelected = useMemo(
		() => complimentary || [],
		[complimentary]
	)

	const occupancyBudget = Math.max(0, (otaRoomType.max_occupancy - ppr) * qty)

	const pprBelowBase = ppr < otaRoomType.base_adult_occupancy

	// No extra-bed-related cap applies here — complimentary occupants don't
	// consume a bed, only physical room occupancy.
	const limitTerms: Array<{ value: number; message: string }> = [
		{
			value:
				occupancyBudget -
				(aweb || 0) -
				cwebSelected.length -
				cnbSelected.length,
			message: "Room occupancy limit reached",
		},
		{
			value:
				otaRoomType.max_child_occupancy * qty -
				cwebSelected.length -
				cnbSelected.length,
			message: "Room's max child occupancy limit reached",
		},
	]
	const max = Math.max(0, Math.min(...limitTerms.map((t) => t.value)))

	return (
		<ChildSlotStepper
			roomTypeId={id}
			bucket="COMP"
			options={options}
			roomTypeSlots={{
				CWEB: cwebSelected,
				CNB: cnbSelected,
				COMP: complimentarySelected,
			}}
			max={max}
			pprBelowBase={pprBelowBase}
			limitMessage={getCapacityLimitMessage(limitTerms)}
			reclassifiedChildSlotIds={reclassifiedChildSlotIds}
		/>
	)
}

const CHILD_SLOT_BUCKETS = {
	CWEB: { label: "CWEB", prefer: "oldest" },
	CNB: { label: "CNB", prefer: "youngest" },
	COMP: { label: "Comp", prefer: "youngest" },
} as const

function getChildSlotFieldName(bucket: TChildSlotBucket, roomTypeId: number) {
	switch (bucket) {
		case "CWEB":
			return `cweb_${roomTypeId}` as const
		case "CNB":
			return `cnb_${roomTypeId}` as const
		case "COMP":
			return `complimentary_${roomTypeId}` as const
	}
}

function ChildSlotStepper({
	roomTypeId,
	bucket,
	options,
	roomTypeSlots,
	max,
	disabled,
	pprBelowBase,
	limitMessage,
	reclassifiedChildSlotIds,
}: {
	roomTypeId: number
	bucket: TChildSlotBucket
	options: Array<IChildSlotOption>
	roomTypeSlots: Record<TChildSlotBucket, Array<IChildSlotOption>>
	max: number
	disabled?: boolean
	pprBelowBase: boolean
	limitMessage: string
	reclassifiedChildSlotIds?: Set<number>
}) {
	const form = useForm<IHotelAvailabilityFormSchema>()
	const {
		statuses,
		freeChildAges,
		unassignedChildAges,
		childSlotOptions,
		dateStrings,
	} = usePaxPlacement()
	const { value: ratePlan } = useFieldValue<
		IHotelAvailabilityFormSchema[`rp_${number}`]
	>(`rp_${roomTypeId}` as const)
	const fieldName = getChildSlotFieldName(bucket, roomTypeId)
	const selected = roomTypeSlots[bucket]
	const { label, prefer } = CHILD_SLOT_BUCKETS[bucket]
	const next = pickChildSlot({
		options,
		excludeIds: Object.values(roomTypeSlots).flatMap((slots) =>
			slots.map((s) => s.id)
		),
		freeChildAges,
		unassignedChildAges,
		prefer,
	})

	// still in a bucket the child no longer qualifies for
	function isFlagged(slot: IChildSlotOption) {
		return (
			Boolean(reclassifiedChildSlotIds?.has(slot.id)) &&
			!options.some((o) => o.id === slot.id)
		)
	}

	function getSwapCandidates(slot: IChildSlotOption) {
		// complimentary children are free, swapping them never changes the price
		if (bucket === "COMP" || !ratePlan || !childSlotOptions) return []
		return getChildSwapCandidates({
			slot,
			bucket,
			roomTypeSlots,
			bucketOptions: childSlotOptions,
			unassignedChildAges,
			rateOption: ratePlan.rateOption,
			dateStrings,
		})
	}

	function swap(slot: IChildSlotOption, candidate: TChildSwapCandidate) {
		form.batch(() => {
			form.change(
				fieldName,
				selected.map((s) => (s.id === slot.id ? candidate.slot : s))
			)
			const { fromBucket } = candidate
			if (fromBucket) {
				form.change(
					getChildSlotFieldName(fromBucket, roomTypeId),
					roomTypeSlots[fromBucket].map((s) =>
						s.id === candidate.slot.id ? slot : s
					)
				)
			}
		})
	}
	const incrementTooltip = !options.length
		? `No child in this trip qualifies for ${label}`
		: pprBelowBase
			? "Fill Base Pax first"
			: selected.length >= max
				? limitMessage
				: !next
					? "All eligible children added"
					: undefined
	const repeatedAge = selected.find((slot) =>
		statuses.some((s) => s.overAges.some((a) => a.age === slot.age))
	)?.age
	const warning = useOverNightsWarning(
		repeatedAge === undefined
			? undefined
			: (s) => s.overAges.some((a) => a.age === repeatedAge),
		(overNights) => {
			const placed = Math.max(
				...overNights.flatMap((s) =>
					s.overAges.filter((a) => a.age === repeatedAge).map((a) => a.placed)
				)
			)
			return `${repeatedAge}y added ${getTimesLabel(placed)}`
		}
	)

	return (
		<Stack gap="1" alignItems="center">
			<QuantityControl
				value={selected.length}
				min={0}
				max={max}
				disabled={disabled}
				disableIncrement={Boolean(incrementTooltip)}
				incrementTooltip={incrementTooltip}
				onChange={(value) => {
					if (value < selected.length) {
						// flagged (age rules changed) children go first, then the last added
						const flagged = selected.filter(isFlagged)
						const toRemove = new Set(
							flagged
								.reverse()
								.concat(selected.filter((s) => !flagged.includes(s)).reverse())
								.slice(0, selected.length - value)
								.map((s) => s.id)
						)
						form.change(
							fieldName,
							selected.filter((s) => !toRemove.has(s.id))
						)
					} else if (next) {
						form.change(fieldName, [...selected, next])
					}
				}}
			/>
			<StepperHints warning={warning} />
			{selected.length ? (
				<Inline gap="1" flexWrap="wrap" justifyContent="center">
					{selected.map((slot) => {
						const candidates = getSwapCandidates(slot)
						const flagged = isFlagged(slot)
						const chip = (
							<Badge
								status={flagged ? "warning" : "primary"}
								style={candidates.length ? { cursor: "pointer" } : undefined}
							>
								{slot.age}y
								{candidates.length ? (
									<>
										{" "}
										<Icons.ChevronDown size="3" />
									</>
								) : null}
							</Badge>
						)
						return (
							<Inline key={slot.id} gap="1" alignItems="center">
								{candidates.length ? (
									<Dropdown noCaret>
										<Dropdown.Toggle
											as="button"
											type="button"
											title="Swap with another child"
										>
											{chip}
										</Dropdown.Toggle>
										<Dropdown.Menu>
											<Dropdown.MenuSectionHeader>
												Swap {slot.age}y with
											</Dropdown.MenuSectionHeader>
											{candidates.map((candidate) => (
												<Dropdown.MenuItem
													key={candidate.slot.id}
													as="button"
													type="button"
													onClick={() => swap(slot, candidate)}
												>
													<Inline gap="2" alignItems="center">
														<Badge status="primary">
															{candidate.slot.age}y
														</Badge>
														<Text as="span" fontSize="xs" color="muted">
															{candidate.fromBucket
																? `currently in ${CHILD_SLOT_BUCKETS[candidate.fromBucket].label}`
																: statuses.some((s) =>
																			s.bedChildAges.includes(
																				candidate.slot.age
																			)
																	  )
																	? "currently in a spare adult bed"
																	: "not placed yet"}
														</Text>
													</Inline>
												</Dropdown.MenuItem>
											))}
										</Dropdown.Menu>
									</Dropdown>
								) : (
									chip
								)}
								{flagged ? (
									<Tooltip content="Age rules changed since this was selected — press − to remove">
										<Box as="span" display="inlineFlex" color="warning">
											<Icons.CircleAlert />
										</Box>
									</Tooltip>
								) : null}
							</Inline>
						)
					})}
				</Inline>
			) : null}
		</Stack>
	)
}

type TPaxPlacementContext = {
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
	otherRows: Array<TPaxOverageRow>
	hotelName: string
	availability: IAvailability
	childSlotOptions: TChildSlotOptions
	dateStrings: Array<string>
}

const PaxPlacementContext = createContext<TPaxPlacementContext | null>(null)

function getModalPaxSelections(
	availability: IAvailability,
	values: IHotelAvailabilityFormSchema
) {
	return availability.room_type_wise_rates.flatMap((rt) => {
		const id = rt.room_type.id
		const qty = values[`qty_${id}`] || 0
		if (!qty) return []
		return [
			{
				qty,
				room_type: { id: rt.room_type.id, name: rt.room_type.name },
				meal_plan: {
					name: values[`rp_${id}`]?.rateOption.meal_plan.name || "",
				},
				occupancy: {
					ppr: values[`ppr_${id}`] || 1,
					aweb: values[`aweb_${id}`] || 0,
					cwebAges: (values[`cweb_${id}`] || []).map((s) => s.age),
					cnbAges: (values[`cnb_${id}`] || []).map((s) => s.age),
					complimentaryAges: (values[`complimentary_${id}`] || []).map(
						(s) => s.age
					),
				},
			},
		]
	})
}

/**
 * Pax placement across other rows and the modal's live selections.
 */
function usePaxPlacement() {
	const context = useContext(PaxPlacementContext)
	const { values } = useFormState<IHotelAvailabilityFormSchema>({
		subscription: { values: true },
	})
	const statuses = useMemo(() => {
		if (!context) return []
		return getNightlyPaxStatus({
			tripNoOfAdults: context.tripNoOfAdults,
			tripChildren: context.tripChildren,
			dateStrings: context.dateStrings,
			existingRows: context.otherRows,
			hotelName: context.hotelName,
			selections: getModalPaxSelections(context.availability, values),
		})
	}, [context, values])
	return {
		statuses,
		freeChildAges: getFreeChildAges(statuses),
		unassignedChildAges: getUnassignedChildAges(statuses),
		childSlotOptions: context?.childSlotOptions,
		dateStrings: context?.dateStrings || [],
	}
}

// e.g. "25–26 Nov, 28 Nov"
function formatNightRuns(dates: Array<string>): string {
	return groupConsecutiveNights(dates.map((date) => ({ date })))
		.map(({ dates: run }) => {
			const first = run[0]
			const last = run[run.length - 1]
			if (!first || !last || first === last) {
				return first ? formatDate(first, "DD MMM") : ""
			}
			return formatDate(first, "MMM") === formatDate(last, "MMM")
				? `${formatDate(first, "DD")}–${formatDate(last, "DD MMM")}`
				: `${formatDate(first, "DD MMM")}–${formatDate(last, "DD MMM")}`
		})
		.join(", ")
}

/**
 * Warning for nights already over, naming them unless all of the modal's
 * nights are affected.
 */
function useOverNightsWarning(
	isOver: ((status: TNightPaxStatus) => boolean) | undefined,
	getMessage: (overNights: Array<TNightPaxStatus>) => string
): string | undefined {
	const { statuses, dateStrings } = usePaxPlacement()
	if (!isOver) return undefined
	const overNights = statuses.filter(isOver)
	if (!overNights.length) return undefined
	const message = getMessage(overNights)
	return overNights.length === dateStrings.length
		? message
		: `${message} on ${formatNightRuns(overNights.map((s) => s.date))}`
}

function getRoomTypeSpareBeds(
	status: TNightPaxStatus | undefined,
	roomTypeId: number
) {
	if (!status) return undefined
	const index = status.allocations.findIndex(
		(a) => a.source === "adding" && a.roomTypeId === roomTypeId
	)
	return index === -1 ? undefined : getSpareBedsByAllocation(status)[index]
}

function useEmptySpareBedWarning(
	kind: "aweb" | "base",
	roomTypeId: number,
	enabled = true
) {
	return useOverNightsWarning(
		enabled
			? (s) => (getRoomTypeSpareBeds(s, roomTypeId)?.[kind].empty || 0) > 0
			: undefined,
		(overNights) =>
			pluralize(
				"extra guest",
				Math.max(
					...overNights.map(
						(s) => getRoomTypeSpareBeds(s, roomTypeId)?.[kind].empty || 0
					)
				),
				true
			)
	)
}

function getSpareBedMessage(ages: Array<number>): string | undefined {
	if (!ages.length) return undefined
	const agesLabel = ages.map((age) => `${age}y`).join(", ")
	return ages.length === 1
		? `Child ${agesLabel} assigned to a spare adult bed`
		: `Children ${agesLabel} assigned to spare adult beds`
}

/**
 * Pax already placed by the quote's other rows on the modal's nights.
 */
function PaxAlreadyPlacedBanner() {
	const context = useContext(PaxPlacementContext)
	const groups = useMemo(() => {
		if (!context) return []
		return groupConsecutiveNights(
			getNightlyPaxStatus({
				tripNoOfAdults: context.tripNoOfAdults,
				tripChildren: context.tripChildren,
				dateStrings: context.dateStrings,
				existingRows: context.otherRows,
				hotelName: context.hotelName,
				selections: [],
			})
		).filter((g) => g.placed.total > 0)
	}, [context])
	if (!groups.length) return null
	return (
		<Alert status="warning">
			<Stack gap="2">
				{groups.map((g) => (
					<Stack key={g.dates.join()} gap="px">
						<Text fontWeight="semibold">
							For {formatNightRuns(g.dates)} –{" "}
							{g.placed.total >= g.trip.total
								? `All ${pluralize("guest", g.trip.total, true)} already placed`
								: `${g.placed.total} of ${g.trip.total} guests already placed`}
						</Text>
						{Array.from(
							new Set(
								g.allocations.map((a) =>
									[a.roomTypeName, a.hotelName].filter(Boolean).join(" · ")
								)
							)
						).map((label) => (
							<Text key={label} fontSize="sm">
								{label}
							</Text>
						))}
					</Stack>
				))}
			</Stack>
		</Alert>
	)
}

/**
 * Running pax count for the tightest night, across other rows and this modal.
 */
function PaxTotalCounter() {
	const { statuses } = usePaxPlacement()
	const worstNight = getWorstNightPaxStatus(statuses)
	if (!worstNight) return null
	const repeated = statuses.flatMap((s) => s.overAges)[0]
	const isOver = worstNight.extra > 0 || Boolean(repeated)
	const remaining = getRemainingPaxLabel(worstNight)
	return (
		<Stack gap="2" textAlign="right">
			<Text
				fontSize="sm"
				fontWeight="semibold"
				color={isOver ? "danger" : remaining ? "warning" : "success"}
			>
				<Icons.Users /> Total Pax {worstNight.placed.total}/
				{worstNight.trip.total}
			</Text>
			<Text fontSize="xs" color="muted">
				{worstNight.extra > 0
					? pluralize("extra guest", worstNight.extra, true)
					: repeated
						? `Child ${repeated.age}y added ${getTimesLabel(repeated.placed)}`
						: remaining || "All guests placed"}
			</Text>
		</Stack>
	)
}

function getRemainingPaxLabel(
	status: Pick<TNightPaxStatus, "remaining">
): string {
	const { adults, childAges } = status.remaining
	const parts: Array<string> = []
	if (adults > 0) parts.push(pluralize("Adult", adults, true))
	if (childAges.length) {
		parts.push(
			`${pluralize("Child", childAges.length, true)} (${childAges
				.map((age) => `${age}y`)
				.join(", ")})`
		)
	}
	return parts.length ? `${parts.join(", ")} remaining` : ""
}

function StepperHints({
	warning,
	info = [],
}: {
	warning?: string
	info?: Array<string | undefined>
}) {
	const infoLines = info.filter((line): line is string => Boolean(line))
	if (!warning && !infoLines.length) return null
	return (
		<Stack gap="px" alignItems="center" textAlign="center">
			{warning ? (
				<Text fontSize="xs" color="warning">
					<Icons.Attention /> {warning}
				</Text>
			) : null}
			{infoLines.map((line) => (
				<Text key={line} fontSize="xs" color="accent">
					<Icons.Info /> {line}
				</Text>
			))}
		</Stack>
	)
}

function RoomGroupPrice({
	id,
	qty,
	ppr,
	dateStrings,
	selectedRatePlan,
	roomType,
}: {
	id: number
	qty: number
	ppr: number
	dateStrings: Array<string>
	selectedRatePlan: IRateOption
	roomType: IHotelRemoteRoomType
}) {
	const { value: aweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`aweb_${number}`]
	>(`aweb_${id}` as const)
	const { value: cweb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cweb_${number}`]
	>(`cweb_${id}` as const)
	const { value: cnb } = useFieldValue<
		IHotelAvailabilityFormSchema[`cnb_${number}`]
	>(`cnb_${id}` as const)
	const { value: complimentary } = useFieldValue<
		IHotelAvailabilityFormSchema[`complimentary_${number}`]
	>(`complimentary_${id}` as const)
	const { value: discount } =
		useFieldValue<IHotelAvailabilityFormSchema["discount"]>("discount")

	const total = useMemo(
		() =>
			getTotalPriceForRoomGroup(
				selectedRatePlan.rateOption,
				roomType,
				dateStrings,
				{
					qty,
					ppr,
					aweb: aweb || 0,
					cwebAges: (cweb || []).map((s) => s.age),
					cnbAges: (cnb || []).map((s) => s.age),
					complimentaryAges: (complimentary || []).map((s) => s.age),
					discountPercent: discount || 0,
				}
			),
		[
			selectedRatePlan,
			roomType,
			dateStrings,
			qty,
			ppr,
			aweb,
			cweb,
			cnb,
			complimentary,
			discount,
		]
	)

	const missingRate = useMemo(() => {
		if (total) return undefined
		return findMissingOccupancyRate(selectedRatePlan.rateOption, dateStrings, {
			aweb: aweb || 0,
			cwebAges: (cweb || []).map((s) => s.age),
			cnbAges: (cnb || []).map((s) => s.age),
		})
	}, [total, selectedRatePlan, dateStrings, aweb, cweb, cnb])

	if (!total) {
		return (
			<Text color="danger" fontSize="sm">
				{missingRate
					? `No rate available for ${OCCUPANCY_PAX_LABELS[missingRate.pax_type]}${
							missingRate.age !== undefined ? ` (age ${missingRate.age})` : ""
						} on ${formatDate(missingRate.date, "D MMM")}`
					: "Rate unavailable for selected occupancy"}
			</Text>
		)
	}

	return (
		<Stack gap="px">
			<Text fontSize="xl" fontWeight="semibold">
				<Money money={total} showCurrency />{" "}
				<Text as="span" fontSize="xs" fontWeight="normal" color="muted">
					(inc. Tax)
				</Text>
			</Text>
			<Text fontSize="xs" color="muted">
				{qty} {pluralize("room", qty)} &times; {dateStrings.length}{" "}
				{pluralize("night", dateStrings.length)}
			</Text>
		</Stack>
	)
}

function RoomTypeSummaryCard({
	roomTypeName,
	mealPlanName,
	rooms,
	nights,
	total,
	breakdown,
}: {
	roomTypeName: string
	mealPlanName: string
	rooms: number
	nights: number
	total: TMoney
	breakdown?: {
		rate: TMoney
		discountAmount?: TMoney
		tax: TMoney
		occupancy: {
			personsPerRoom: number
			awebCount: number
			cwebAges: Array<number>
			cnbAges: Array<number>
			complimentaryAges: Array<number>
		}
	}
}) {
	const occupancySummary = breakdown
		? [
				breakdown.occupancy.personsPerRoom
					? `${breakdown.occupancy.personsPerRoom} Adults/Room`
					: null,
				breakdown.occupancy.awebCount > 0
					? `${breakdown.occupancy.awebCount} AWEB`
					: null,
				breakdown.occupancy.cwebAges.length
					? `CWEB (${breakdown.occupancy.cwebAges.map((a) => `${a}y`).join(", ")})`
					: null,
				breakdown.occupancy.cnbAges.length
					? `CNB (${breakdown.occupancy.cnbAges.map((a) => `${a}y`).join(", ")})`
					: null,
				breakdown.occupancy.complimentaryAges.length
					? `Complimentary (${breakdown.occupancy.complimentaryAges.map((a) => `${a}y`).join(", ")})`
					: null,
			]
				.filter(Boolean)
				.join(" + ")
		: ""

	return (
		<Box borderWidth="1" rounded="md" padding="3">
			<Stack gap="2">
				<Inline justifyContent="between" alignItems="start" gap="4">
					<Text fontWeight="semibold" fontSize="sm">
						{roomTypeName}
					</Text>
					<Stack gap="px" style={{ textAlign: "right" }}>
						<Text fontSize="xs" color="muted">
							{mealPlanName}
						</Text>
						<Text fontSize="xs" color="muted">
							{rooms} {pluralize("room", rooms)} &times; {nights}{" "}
							{pluralize("night", nights)}
						</Text>
					</Stack>
				</Inline>

				{breakdown ? (
					<Stack gap="1">
						<Inline justifyContent="between" alignItems="start" gap="4">
							<Text fontSize="xs" color="muted">
								{occupancySummary}
							</Text>
							<Text fontSize="xs">
								<Money money={breakdown.rate} />
							</Text>
						</Inline>
						{breakdown.discountAmount ? (
							<Inline justifyContent="between" alignItems="center">
								<Text fontSize="xs" color="success">
									Discount
								</Text>
								<Text fontSize="xs" color="success">
									-<Money money={breakdown.discountAmount} />
								</Text>
							</Inline>
						) : null}
						<Inline justifyContent="between" alignItems="center">
							<Text fontSize="xs" color="muted">
								Tax
							</Text>
							<Text fontSize="xs">
								<Money money={breakdown.tax} />
							</Text>
						</Inline>
						<Inline
							justifyContent="between"
							alignItems="center"
							borderTopWidth="1"
							paddingTop="1"
						>
							<Text fontSize="xs" color="muted">
								Total
							</Text>
							<Text fontSize="sm" fontWeight="semibold">
								<Money money={total} showCurrency />
							</Text>
						</Inline>
					</Stack>
				) : (
					<Inline justifyContent="between" alignItems="center">
						<Text fontSize="xs" color="muted">
							Total
						</Text>
						<Text fontSize="sm" fontWeight="semibold">
							<Money money={total} showCurrency />
						</Text>
					</Inline>
				)}
			</Stack>
		</Box>
	)
}

// Sum across every room+night gives the true pre-tax total regardless of
// whether nightly rates or tiers varied — no itemized per-component display,
// just the total, re-used for both the discounted and the before-discount
// figure (same shape, different input).
function sumRbpPreTaxTotal(
	breakdowns: Array<Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>>
): TMoney {
	const parts = [
		...breakdowns.map((b) => b.base.amount),
		...breakdowns
			.map((b) => b.aweb?.amount)
			.filter((a): a is TMoney => Boolean(a)),
		...breakdowns
			.map((b) => b.cweb?.amount)
			.filter((a): a is TMoney => Boolean(a)),
		...breakdowns
			.map((b) => b.cnb?.amount)
			.filter((a): a is TMoney => Boolean(a)),
	]
	return addMoney(parts[0], ...parts.slice(1))
}

function GrandTotal({
	availability,
	dateStrings,
}: {
	availability: IAvailability
	dateStrings: Array<string>
}) {
	return (
		<FormSpy<IHotelAvailabilityFormSchema> subscription={{ values: true }}>
			{({ values }) => {
				const discountPercent = values.discount || 0

				const summaries: Array<{
					roomTypeName: string
					mealPlanName: string
					rooms: number
					nights: number
					total: TMoney
					breakdown?: {
						rate: TMoney
						discountAmount?: TMoney
						tax: TMoney
						occupancy: {
							personsPerRoom: number
							awebCount: number
							cwebAges: Array<number>
							cnbAges: Array<number>
							complimentaryAges: Array<number>
						}
					}
				}> = []

				for (const rt of availability.room_type_wise_rates) {
					const id = rt.room_type.id
					const qty = values[`qty_${id}`] || 0
					const selectedRatePlan = values[`rp_${id}`]
					if (qty <= 0 || !selectedRatePlan) continue

					const ppr = values[`ppr_${id}`] || 1
					const aweb = values[`aweb_${id}`] || 0
					const cweb = values[`cweb_${id}`] || []
					const cnb = values[`cnb_${id}`] || []
					const complimentary = values[`complimentary_${id}`] || []

					const occupancyArgs = {
						qty,
						ppr,
						aweb,
						cwebAges: cweb.map((s) => s.age),
						cnbAges: cnb.map((s) => s.age),
						complimentaryAges: complimentary.map((s) => s.age),
					}

					// Tax is applied on the discounted rate (see the tax phase) and
					// reflected in `total` and `breakdown` below.
					const nightly = getNightlyPricesForRoomGroup(
						selectedRatePlan.rateOption,
						rt.room_type,
						dateStrings,
						{ ...occupancyArgs, discountPercent }
					)
					if (!nightly?.length) continue

					// Only computed when a discount is active, purely to show how
					// much it saved — the rack (pre-discount) rate isn't stored
					// anywhere, so this is the same figures re-derived at 0% discount.
					const nightlyBeforeDiscount =
						discountPercent > 0
							? getNightlyPricesForRoomGroup(
									selectedRatePlan.rateOption,
									rt.room_type,
									dateStrings,
									occupancyArgs
								)
							: undefined

					const total = addMoney(
						nightly[0].total,
						...nightly.slice(1).map((n) => n.total)
					)

					// Only build a line-item breakdown when every night is RBP — RBP
					// nights have genuine, separate base/AWEB/CWEB/CNB components;
					// OBP nights price adults+children as one combined tier lookup
					// with no real split to show. Mixing the two would mean either
					// fabricating a split for OBP nights or silently under-reporting
					// a component, so any OBP night falls back to just the one
					// combined, always-correct total (same as before this change).
					const allRoomBreakdowns = nightly.flatMap((n) =>
						n.rooms.map((r) => r.breakdown)
					)
					const allRbp = allRoomBreakdowns.every(
						(b) => b.pricing_type === "RBP"
					)

					let breakdown: (typeof summaries)[number]["breakdown"]

					if (allRbp) {
						const rbpBreakdowns = allRoomBreakdowns as Array<
							Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>
						>
						const preTaxTotal = sumRbpPreTaxTotal(rbpBreakdowns)

						// `rate` is what the summary line shows next to the occupancy
						// label — always the pre-discount figure, defaulting to
						// `preTaxTotal` itself when there's no discount to back out.
						let rate = preTaxTotal
						let discountAmount: TMoney | undefined
						if (nightlyBeforeDiscount?.length) {
							const beforeDiscountBreakdowns = nightlyBeforeDiscount
								.flatMap((n) => n.rooms.map((r) => r.breakdown))
								.every((b) => b.pricing_type === "RBP")
								? (nightlyBeforeDiscount.flatMap((n) =>
										n.rooms.map((r) => r.breakdown)
									) as Array<
										Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>
									>)
								: undefined
							if (beforeDiscountBreakdowns) {
								rate = sumRbpPreTaxTotal(beforeDiscountBreakdowns)
								discountAmount = subtractMoney(rate, preTaxTotal)
							}
						}

						const taxAmounts = rbpBreakdowns
							.map((b) => b.tax?.amount)
							.filter((a): a is TMoney => Boolean(a))

						breakdown = {
							rate,
							discountAmount,
							tax: taxAmounts.length
								? addMoney(taxAmounts[0], ...taxAmounts.slice(1))
								: createMoney(0, total.currency),
							occupancy: {
								personsPerRoom: ppr,
								awebCount: aweb,
								cwebAges: cweb.map((s) => s.age),
								cnbAges: cnb.map((s) => s.age),
								complimentaryAges: complimentary.map((s) => s.age),
							},
						}
					}

					summaries.push({
						roomTypeName: rt.room_type.name,
						mealPlanName: selectedRatePlan.rateOption.meal_plan.name,
						rooms: qty,
						nights: dateStrings.length,
						total,
						breakdown,
					})
				}

				if (!summaries.length) return null

				const summed = addMoneyFromDifferentCurrencies(
					summaries.map((s) => s.total)
				)

				return (
					<Stack gap="2">
						<Text fontWeight="semibold">Price Summary</Text>

						{summaries.map((s, i) => (
							<RoomTypeSummaryCard key={i} {...s} />
						))}

						<Inline
							justifyContent="between"
							alignItems="center"
							paddingY="2"
							borderTopWidth="1"
						>
							<Text fontWeight="semibold">Total</Text>
							<Stack gap="px" style={{ textAlign: "right" }}>
								<Text fontWeight="semibold" fontSize="lg">
									<MoneySum money={summed} />
								</Text>
								<Text fontSize="xs" color="muted">
									(inc. Tax)
								</Text>
							</Stack>
						</Inline>
					</Stack>
				)
			}}
		</FormSpy>
	)
}

// e.g. "2 CNB (10y, 7y)"
function formatChildCount(
	count: number,
	label: string,
	ages: Array<number> | null | undefined
): string {
	return ages?.length
		? `${count} ${label} (${ages.map((age) => `${age}y`).join(", ")})`
		: `${count} ${label}`
}

export function HotelAvailabilitySelectionField({
	name,
	index,
	tripId,
	tripNoOfAdults,
	tripChildren,
}: {
	name: string
	index: number
	tripId: number
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
}) {
	const form =
		useForm<INewQuoteState["hotel_options"][number]["hotels"][number]>()
	const { value: hotel } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["hotel"]
	>(`${name}.hotel`)
	const { value: dates } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["dates"]
	>(`${name}.dates`)
	const { value: roomType } = useFieldValue<IHotelRemoteRoomType | undefined>(
		`${name}.room_type`
	)
	const { value: mealPlan } = useFieldValue<IHotelRemoteMealPlan | undefined>(
		`${name}.meal_plan`
	)
	const { value: noOfRooms } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["no_of_rooms"]
	>(`${name}.no_of_rooms`)
	const { value: personsPerRoom } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["persons_per_room"]
	>(`${name}.persons_per_room`)
	const { value: aweb } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["adults_with_extra_bed"]
	>(`${name}.adults_with_extra_bed`)
	const { value: cweb } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["children_with_extra_bed"]
	>(`${name}.children_with_extra_bed`)
	const { value: cwebAges } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["children_with_extra_bed_ages"]
	>(`${name}.children_with_extra_bed_ages`)
	const { value: cnb } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["children_without_extra_bed"]
	>(`${name}.children_without_extra_bed`)
	const { value: cnbAges } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["children_without_extra_bed_ages"]
	>(`${name}.children_without_extra_bed_ages`)
	const { value: complimentaryAges } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["children_complimentary_ages"]
	>(`${name}.children_complimentary_ages`)

	const { value: dateWisePrices } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotels"][number]["date_wise_prices"]
	>(`${name}.date_wise_prices`)

	const remoteConfig = (dateWisePrices || []).length
		? dateWisePrices[0].remote_config
		: undefined

	const dateStrings = (dates || []).map((d) => formatDate(d, "YYYY-MM-DD"))
	const { data: availabilityResponse } = useHotelAvailability(
		hotel?.id,
		dateStrings,
		tripChildren,
		tripId
	)
	const availability = availabilityResponse?.data
	const staleWarning = useMemo(() => {
		if (
			!availability ||
			!roomType ||
			!mealPlan ||
			!remoteConfig ||
			!dateWisePrices?.length
		)
			return null
		return detectStaleAvailability({
			availability,
			otaRoomTypeId: remoteConfig.ota_room_type_id,
			otaMealPlanId: remoteConfig.ota_meal_plan_id,
			savedRoomType: roomType,
			savedMealPlan: mealPlan,
			noOfRooms: noOfRooms || 0,
			storedDateWisePrices: dateWisePrices.map((p) => ({
				date: formatDate(p.date, "YYYY-MM-DD"),
				remote_price_breakdowns: p.remote_price_breakdowns,
			})),
		})
	}, [
		availability,
		roomType,
		mealPlan,
		remoteConfig,
		dateWisePrices,
		noOfRooms,
	])

	const handleConfirm = useCallback(
		(selections: Array<IConfirmedRoomSelection>) => {
			const [first, ...rest] = selections
			const firstLineItem = mapConfirmedSelectionToLineItem(first)

			form.batch(() => {
				form.change(
					`${name}.room_type` as never,
					firstLineItem.room_type as never
				)
				form.change(
					`${name}.meal_plan` as never,
					firstLineItem.meal_plan as never
				)
				form.change(
					`${name}.no_of_rooms` as never,
					firstLineItem.no_of_rooms as never
				)
				form.change(
					`${name}.persons_per_room` as never,
					firstLineItem.persons_per_room as never
				)
				form.change(
					`${name}.adults_with_extra_bed` as never,
					firstLineItem.adults_with_extra_bed as never
				)
				form.change(
					`${name}.children_with_extra_bed` as never,
					firstLineItem.children_with_extra_bed as never
				)
				form.change(
					`${name}.children_with_extra_bed_ages` as never,
					firstLineItem.children_with_extra_bed_ages as never
				)
				form.change(
					`${name}.children_without_extra_bed` as never,
					firstLineItem.children_without_extra_bed as never
				)
				form.change(
					`${name}.children_without_extra_bed_ages` as never,
					firstLineItem.children_without_extra_bed_ages as never
				)
				form.change(
					`${name}.children_complimentary_ages` as never,
					firstLineItem.children_complimentary_ages as never
				)
				form.change(
					`${name}.date_wise_prices` as never,
					firstLineItem.date_wise_prices as never
				)
			})

			if (rest.length) {
				const allHotels =
					getFormValueForKey(form.getState().values, "hotels") || []
				const currentEntry = allHotels[index]
				const newHotels = [...allHotels]

				rest.forEach((sel, i) => {
					const lineItem = mapConfirmedSelectionToLineItem(sel)
					newHotels.splice(index + i + 1, 0, {
						...currentEntry,
						...lineItem,
						comments: "",
						similar_hotel_options: [],
					})
				})

				form.change("hotels" as never, newHotels as never)
			}
		},
		[name, index, form]
	)

	const getOtherRows = useCallback((): Array<TPaxOverageRow> => {
		const allHotels: Array<TPaxOverageRow> =
			getFormValueForKey(form.getState().values, "hotels") || []
		return allHotels.filter((_, i) => i !== index)
	}, [form, index])

	const prevHotelIdRef = useRef(hotel?.id)
	const prevDatesLengthRef = useRef(dates?.length)
	useEffect(() => {
		if (
			prevHotelIdRef.current !== hotel?.id ||
			prevDatesLengthRef.current !== dates?.length
		) {
			if (roomType) {
				form.batch(() => {
					form.change(`${name}.room_type` as never, undefined as never)
					form.change(`${name}.meal_plan` as never, undefined as never)
					form.change(`${name}.date_wise_prices` as never, undefined as never)
					form.change(`${name}.persons_per_room` as never, 2 as never)
					form.change(`${name}.adults_with_extra_bed` as never, 0 as never)
					form.change(`${name}.children_with_extra_bed` as never, 0 as never)
					form.change(
						`${name}.children_with_extra_bed_ages` as never,
						undefined as never
					)
					form.change(`${name}.children_without_extra_bed` as never, 0 as never)
					form.change(
						`${name}.children_without_extra_bed_ages` as never,
						undefined as never
					)
					form.change(
						`${name}.children_complimentary_ages` as never,
						undefined as never
					)
				})
			}
			prevHotelIdRef.current = hotel?.id
			prevDatesLengthRef.current = dates?.length
		}
	}, [hotel?.id, dates?.length, name, form, roomType])

	if (!hotel || !dates?.length) return null

	const existingSelection =
		roomType && mealPlan && remoteConfig
			? {
					roomType: {
						...roomType,
						ota_room_type_id: remoteConfig?.ota_room_type_id,
					},
					mealPlan: {
						...mealPlan,
						ota_meal_plan_id: remoteConfig?.ota_meal_plan_id,
					},
					remoteConfig,
					noOfRooms,
					personsPerRoom,
					aweb,
					cwebAges: cwebAges || [],
					cnbAges: cnbAges || [],
					complimentaryAges: complimentaryAges || [],
				}
			: undefined

	return (
		<Stack gap="2">
			{!(roomType && mealPlan) ? (
				<Box
					paddingY="4"
					paddingX="4"
					borderWidth="1"
					rounded="md"
					bgColor="subtle"
				>
					<Stack gap="2" alignItems="center">
						<Text color="muted">No room type selected yet</Text>
						<HotelAvailabilityInDialog
							hotel={hotel}
							dates={dates}
							tripId={tripId}
							tripNoOfAdults={tripNoOfAdults}
							tripChildren={tripChildren}
							onConfirm={handleConfirm}
							getOtherRows={getOtherRows}
						>
							{({ setDialogOpen }) => (
								<Button onClick={() => setDialogOpen()}>
									Check Availability &amp; Select Room
								</Button>
							)}
						</HotelAvailabilityInDialog>
					</Stack>
				</Box>
			) : (
				<Stack gap="2">
					{staleWarning ? (
						<Alert status="error">{staleWarning.message}</Alert>
					) : null}
					<Inline
						gap="2"
						alignItems="center"
						justifyContent="between"
						paddingY="2"
						paddingX="2"
						borderWidth="1"
						rounded="md"
						flexWrap="wrap"
					>
						<Inline alignItems="center" gap="2" fontSize="xs" flexWrap="wrap">
							<Text fontWeight="semibold" fontSize="sm">
								{roomType.name}
							</Text>
							<Text>{mealPlan.name}</Text>
							<Text>
								{noOfRooms} {pluralize("Room", noOfRooms)}
							</Text>
							{personsPerRoom > 1 ||
							aweb > 0 ||
							cweb > 0 ||
							cnb > 0 ||
							(complimentaryAges || []).length ? (
								<Text fontSize="sm" color="muted">
									{[
										personsPerRoom > 1 ? `${personsPerRoom} Pax/Room` : null,
										aweb > 0 ? `${aweb} AWEB` : null,
										cweb > 0 ? formatChildCount(cweb, "CWEB", cwebAges) : null,
										cnb > 0 ? formatChildCount(cnb, "CNB", cnbAges) : null,
										(complimentaryAges || []).length > 0
											? formatChildCount(
													(complimentaryAges || []).length,
													"COMP",
													complimentaryAges
												)
											: null,
									]
										.filter(Boolean)
										.join(" + ")}
								</Text>
							) : null}
							<Badge status="success">Available</Badge>
							{dateWisePrices?.length ? (
								<PriceBreakupTooltip dateWisePrices={dateWisePrices} />
							) : null}
						</Inline>
						<HotelAvailabilityInDialog
							hotel={hotel}
							dates={dates}
							tripId={tripId}
							tripNoOfAdults={tripNoOfAdults}
							tripChildren={tripChildren}
							onConfirm={handleConfirm}
							existingSelection={existingSelection}
							getOtherRows={getOtherRows}
						>
							{({ setDialogOpen }) => (
								<Button
									size="sm"
									level="secondary"
									onClick={() => setDialogOpen()}
								>
									Change
								</Button>
							)}
						</HotelAvailabilityInDialog>
					</Inline>
				</Stack>
			)}
		</Stack>
	)
}

// e.g. "21–23 Nov", "30 Nov–1 Dec"
function formatNightRange(dates: Array<string>): string {
	const first = dates[0]
	const last = dates[dates.length - 1]
	if (!first || !last) return ""
	if (first === last) return formatDate(first, "D MMM")
	const sameMonth =
		formatDate(first, "MMM YYYY") === formatDate(last, "MMM YYYY")
	return `${formatDate(first, sameMonth ? "D" : "D MMM")}–${formatDate(last, "D MMM")}`
}

/**
 * Night-wise price breakup of a saved selection at given rates; consecutive
 * nights with the same prices are grouped.
 */
function PriceBreakupTooltip({
	dateWisePrices,
}: {
	dateWisePrices: Array<IDaywiseHotelPrice>
}) {
	const breakups = dateWisePrices.map((p) =>
		getNightPriceBreakup({ ...p, date: formatDate(p.date, "YYYY-MM-DD") })
	)
	const nights = breakups.filter((b): b is NonNullable<typeof b> => Boolean(b))
	// partial breakups would show a wrong grand total
	if (!nights.length || nights.length !== breakups.length) return null
	const groups = groupConsecutiveNights(nights)
	const [firstTotal, ...restTotals] = groups.map((g) =>
		multipleMoneyBy(g.total, g.dates.length)
	)
	if (!firstTotal) return null
	const grandTotal = addMoney(firstTotal, ...restTotals)

	return (
		<Tooltip
			interactive
			content={
				<Stack gap="3" style={{ maxHeight: "320px", overflowY: "auto" }}>
					{groups.map((group) => (
						<Stack key={group.dates[0]} gap="1">
							<Text fontSize="sm" fontWeight="semibold">
								{formatNightRange(group.dates)}
								{group.dates.length > 1 ? (
									<Text
										as="span"
										fontSize="xs"
										fontWeight="normal"
										color="muted"
									>
										{" "}
										· per night
									</Text>
								) : null}
							</Text>
							<NightPriceBreakupLines breakup={group} />
						</Stack>
					))}
					<Inline
						gap="4"
						justifyContent="between"
						borderTopWidth="1"
						paddingTop="2"
					>
						<Text fontSize="sm" fontWeight="semibold">
							Grand Total ({pluralize("night", nights.length, true)})
						</Text>
						<Text fontSize="sm" fontWeight="semibold">
							<Money money={grandTotal} />
							<Text as="span" fontSize="xs" fontWeight="normal" color="muted">
								{" "}
								(inc. Tax)
							</Text>
						</Text>
					</Inline>
				</Stack>
			}
		>
			<Text
				as="span"
				fontSize="sm"
				color="accent"
				style={{ cursor: "help", textDecoration: "underline dotted" }}
			>
				Price details
			</Text>
		</Tooltip>
	)
}

function NightPriceBreakupLines({
	breakup,
}: {
	breakup: Omit<TNightPriceBreakup, "date">
}) {
	return (
		<>
			{breakup.base ? (
				<PriceBreakupLine
					label="Base"
					detail={
						<>
							{breakup.base.quantity} &times;{" "}
							<Money money={breakup.base.rate} />
						</>
					}
					originalRate={breakup.base.originalRate}
					amount={breakup.base.amount}
				/>
			) : null}
			{breakup.aweb ? (
				<PriceBreakupLine
					label="AWEB"
					detail={
						<>
							{breakup.aweb.quantity} &times;{" "}
							<Money money={breakup.aweb.rate} />
						</>
					}
					originalRate={breakup.aweb.originalRate}
					amount={breakup.aweb.amount}
				/>
			) : null}
			{(["cweb", "cnb"] as const).flatMap((kind) =>
				breakup[kind].map((line, i) => (
					<PriceBreakupLine
						key={`${kind}-${i}`}
						label={i === 0 ? kind.toUpperCase() : ""}
						detail={`${line.age ?? "—"}y · ${line.occupancy}`}
						originalRate={line.originalRate}
						amount={line.amount}
					/>
				))
			)}
			{breakup.compAges.length ? (
				<PriceBreakupLine
					label="Comp"
					detail={breakup.compAges.map((a) => `${a}y`).join(", ")}
					amount="Free"
				/>
			) : null}
			<PriceBreakupLine label="Tax" amount={breakup.tax} />
			<PriceBreakupLine
				label="Total"
				amount={breakup.total}
				amountSuffix="(inc. Tax)"
				strong
			/>
		</>
	)
}

/**
 * Price breakup of a single quote hotel night on hover; renders just the
 * children when the night has no remote (RBP) breakdown.
 */
export function QuoteHotelNightPriceBreakup({
	price,
	rateType,
	children,
}: {
	price?: Parameters<typeof getNightPriceBreakup>[0]
	rateType: "given" | "calculated"
	children: React.ReactNode
}) {
	const breakup = price ? getNightPriceBreakup(price, rateType) : undefined
	if (!breakup) return <>{children}</>
	return (
		<Tooltip
			interactive
			content={
				<Stack gap="1">
					<Text fontSize="sm" fontWeight="semibold">
						{rateType === "given" ? "Given Price" : "System Price"}
					</Text>
					<NightPriceBreakupLines breakup={breakup} />
				</Stack>
			}
		>
			<Box as="span" display="inlineFlex" style={{ cursor: "help" }}>
				{children}
			</Box>
		</Tooltip>
	)
}

function PriceBreakupLine({
	label,
	detail,
	originalRate,
	amount,
	amountSuffix,
	strong,
}: {
	label: string
	detail?: React.ReactNode
	originalRate?: TMoney
	amount: TMoney | string
	amountSuffix?: string
	strong?: boolean
}) {
	return (
		<Inline gap="3" alignItems="baseline">
			<Text
				fontSize="sm"
				fontWeight={strong ? "semibold" : undefined}
				style={{ width: "48px" }}
			>
				{label}
			</Text>
			<Box flex="1">
				<Text fontSize="xs" color="muted">
					{detail}
					{originalRate ? (
						<>
							{" "}
							<Text as="s">
								<Money money={originalRate} />
							</Text>
						</>
					) : null}
				</Text>
			</Box>
			<Text
				fontSize="sm"
				fontWeight={strong ? "semibold" : undefined}
				style={{ textAlign: "right" }}
			>
				{typeof amount === "string" ? amount : <Money money={amount} />}
				{amountSuffix ? (
					<Text as="span" fontSize="xs" fontWeight="normal" color="muted">
						{" "}
						{amountSuffix}
					</Text>
				) : null}
			</Text>
		</Inline>
	)
}

type TGivenPriceOccupancy = {
	noOfRooms: number
	personsPerRoom: number
	awebCount: number
	cwebAges: Array<number>
	cnbAges: Array<number>
}

type TGivenPriceConstraints = {
	baseOccupancy: number
	allowedExtraBeds: number
	allowedAwebPerRoom: number
	allowedCwebPerRoom: number
	allowedCnbPerRoom: number | null
	maxAdultOccupancy: number
	maxChildOccupancy: number
}

type TGivenPriceFormValues = {
	base_given_rate: number
	aweb_given_rate?: number

	cweb_given_rates?: Array<{ age: number; rate: number }>
	cnb_given_rates?: Array<{ age: number; rate: number }>
	keep_same_for_other_nights?: boolean
}

function uniqueAges(ages: Array<number>): Array<number> {
	return Array.from(new Set(ages)).sort((a, b) => a - b)
}

function countByAge(ages: Array<number>, age: number): number {
	return ages.filter((a) => a === age).length
}

function findBreakdownRow(
	breakdown: THotelPriceBreakdowns,
	paxType: THotelPaxTypeCode,
	age?: number
) {
	return breakdown.find(
		(r) => r.pax_type === paxType && (age === undefined || r.age === age)
	)
}

// The base-occupancy row is "BASE" normally, or "A" when priced at a
// below-base-occupancy tier (e.g. "A1") — see mapRbpBreakdownToRemote.
function findBaseBreakdownRow(breakdown: THotelPriceBreakdowns) {
	return breakdown.find((r) => r.pax_type === "BASE" || r.pax_type === "A")
}

export function RemoteGivenPriceInputField({
	date,
	currency,
	breakdown,
	given_price,
	occupancy,
	constraints,
	roomStays,
	hasOtherNights,
	onChange,
}: {
	date: Date | string
	currency: string
	breakdown: THotelPriceBreakdowns
	given_price: number
	edited_given_price?: boolean
	occupancy: TGivenPriceOccupancy
	constraints: TGivenPriceConstraints
	roomStays: ReturnType<typeof mapRoomWiseBreakdownToRemote>
	hasOtherNights: boolean
	onChange: (
		update: {
			price_breakdowns: THotelPriceBreakdowns
			room_stays: ReturnType<typeof mapRoomWiseBreakdownToRemote>
			given_price: number
			edited_given_price: true
		},
		keepSameForOtherNights: boolean
	) => void
}) {
	const [dialogOpen, openDialog, closeDialog] = useDialog()
	const [roomAllocationExpanded, setRoomAllocationExpanded] = useState(false)

	const cwebAges = useMemo(
		() => uniqueAges(occupancy.cwebAges),
		[occupancy.cwebAges]
	)
	const cnbAges = useMemo(
		() => uniqueAges(occupancy.cnbAges),
		[occupancy.cnbAges]
	)

	const initialValues: TGivenPriceFormValues = useMemo(
		() => ({
			base_given_rate: findBaseBreakdownRow(breakdown)?.given_rate_bt ?? 0,
			aweb_given_rate: findBreakdownRow(breakdown, "AWEB")?.given_rate_bt,
			cweb_given_rates: cwebAges.map((age) => ({
				age,
				rate: findBreakdownRow(breakdown, "CWEB", age)?.given_rate_bt ?? 0,
			})),
			cnb_given_rates: cnbAges.map((age) => ({
				age,
				rate: findBreakdownRow(breakdown, "CNB", age)?.given_rate_bt ?? 0,
			})),
			keep_same_for_other_nights: false,
		}),
		[breakdown, cwebAges, cnbAges]
	)

	return (
		<>
			<Button size="sm" level={"secondary"} onClick={openDialog}>
				<Money currency={currency} amount={given_price} showCurrency />
			</Button>
			<Dialog
				open={dialogOpen}
				onClose={closeDialog}
				title={`Given Price - ${formatDate(date, "DD MMM")}`}
				sm
			>
				<Dialog.Body>
					<Form<TGivenPriceFormValues>
						initialValues={initialValues}
						validate={validateFormValues(
							Validator.object().shape({
								base_given_rate: EmptyNumberValidator()
									.required("Please provide the room rate")
									.min(0, "Rate can not be negative."),
								aweb_given_rate: findBreakdownRow(breakdown, "AWEB")
									? EmptyNumberValidator()
											.required("Please provide the AWEB rate")
											.min(0, "Rate can not be negative.")
									: Validator.number().nullable(),
								cweb_given_rates: breakdown.some((r) => r.pax_type === "CWEB")
									? Validator.array().of(
											Validator.object().shape({
												age: Validator.number().required(),
												rate: EmptyNumberValidator()
													.required("Please provide a rate for this age")
													.min(0, "Rate can not be negative."),
											})
										)
									: Validator.array().nullable(),
								cnb_given_rates: breakdown.some((r) => r.pax_type === "CNB")
									? Validator.array().of(
											Validator.object().shape({
												age: Validator.number().required(),
												rate: EmptyNumberValidator()
													.required("Please provide a rate for this age")
													.min(0, "Rate can not be negative."),
											})
										)
									: Validator.array().nullable(),
							})
						)}
						onSubmit={(values) => {
							const newBreakdown: THotelPriceBreakdowns = breakdown.map(
								(row) => {
									if (row.pax_type === "BASE" || row.pax_type === "A") {
										return { ...row, given_rate_bt: values.base_given_rate }
									}
									if (row.pax_type === "AWEB") {
										return {
											...row,
											given_rate_bt:
												values.aweb_given_rate ?? row.given_rate_bt,
										}
									}
									if (row.pax_type === "CWEB") {
										return {
											...row,
											given_rate_bt:
												values.cweb_given_rates?.find((e) => e.age === row.age)
													?.rate ?? row.given_rate_bt,
										}
									}
									if (row.pax_type === "CNB") {
										return {
											...row,
											given_rate_bt:
												values.cnb_given_rates?.find((e) => e.age === row.age)
													?.rate ?? row.given_rate_bt,
										}
									}
									return row
								}
							)

							const taxResult = computeRoomAllocationAndTaxForGivenRates(
								occupancy,
								constraints,
								{
									baseRate: moneyParseByDecimal(
										values.base_given_rate,
										currency
									),
									awebRate:
										values.aweb_given_rate !== undefined
											? moneyParseByDecimal(values.aweb_given_rate, currency)
											: undefined,
									cwebRatesByAge: new Map(
										(values.cweb_given_rates || []).map((e) => [
											e.age,
											moneyParseByDecimal(e.rate, currency),
										])
									),
									cnbRatesByAge: new Map(
										(values.cnb_given_rates || []).map((e) => [
											e.age,
											moneyParseByDecimal(e.rate, currency),
										])
									),
								}
							)
							if (!taxResult) return

							const newRoomStays = buildRoomStaysForGivenRates(
								newBreakdown,
								taxResult,
								occupancy,
								constraints,
								roomStays,
								currency
							)

							onChange(
								{
									price_breakdowns: newBreakdown,
									room_stays: newRoomStays,
									given_price: Number(
										formatMoneyByDecimal(taxResult.winner.totalAfterTax)
									),
									edited_given_price: true,
								},
								Boolean(values.keep_same_for_other_nights)
							)
							closeDialog()
						}}
					>
						{({ handleSubmit }) => {
							const baseRow = findBaseBreakdownRow(breakdown)
							if (!baseRow) return null
							const awebRow = findBreakdownRow(breakdown, "AWEB")

							const rows: Array<Array<React.ReactNode>> = [
								[
									<Text fontSize="sm" fontWeight="semibold">
										Base ({baseRow.occupancy})
									</Text>,
									<Text fontSize="sm" color="muted">
										<Money
											currency={currency}
											amount={baseRow.calculated_rate_bt}
										/>
									</Text>,
									<TextInputField
										name="base_given_rate"
										type="number"
										style={{ width: "100px" }}
										min={0}
									/>,
									baseRow.quantity,
									<GetFieldValue<
										TGivenPriceFormValues["base_given_rate"]
									> name="base_given_rate">
										{({ value: baseGivenRate }) =>
											baseGivenRate ? (
												<Text>
													<Money
														money={multipleMoneyBy(
															moneyParseByDecimal(
																Number(baseGivenRate),
																currency
															),
															baseRow.quantity
														)}
													/>
												</Text>
											) : (
												<Text>-</Text>
											)
										}
									</GetFieldValue>,
								],
							]
							if (awebRow) {
								const awebQuantity = awebRow.quantity
								rows.push([
									<Text fontSize="sm">AWEB</Text>,
									<Text fontSize="sm" color="muted">
										<Money
											currency={currency}
											amount={awebRow.calculated_rate_bt}
										/>
									</Text>,
									<TextInputField
										name="aweb_given_rate"
										type="number"
										style={{ width: "100px" }}
										min={0}
									/>,
									awebQuantity,
									<GetFieldValue<
										TGivenPriceFormValues["aweb_given_rate"] | undefined
									> name="aweb_given_rate">
										{({ value: awebGivenRate }) =>
											awebGivenRate ? (
												<Text>
													<Money
														money={multipleMoneyBy(
															moneyParseByDecimal(
																Number(awebGivenRate),
																currency
															),
															awebQuantity
														)}
													/>
												</Text>
											) : (
												<Text>-</Text>
											)
										}
									</GetFieldValue>,
								])
							}
							if (breakdown.some((r) => r.pax_type === "CWEB")) {
								cwebAges.forEach((age, index) => {
									const child = findBreakdownRow(breakdown, "CWEB", age)
									const fieldName = `cweb_given_rates[${index}].rate`
									const quantity = countByAge(occupancy.cwebAges, age)
									rows.push([
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold">
												CWEB — {age}y
											</Text>
											<Text fontSize="xs" color="muted">
												{child?.occupancy}
											</Text>
										</Stack>,
										<Text fontSize="sm" color="muted">
											<Money
												currency={currency}
												amount={child?.calculated_rate_bt || 0}
											/>
										</Text>,
										<TextInputField
											name={fieldName}
											type="number"
											style={{ width: "100px" }}
											min={0}
										/>,
										quantity,
										<GetFieldValue<
											| Required<TGivenPriceFormValues>["cweb_given_rates"][number]["rate"]
											| undefined
										>
											name={fieldName}
										>
											{({ value }) =>
												value ? (
													<Text>
														<Money
															money={multipleMoneyBy(
																moneyParseByDecimal(Number(value), currency),
																quantity
															)}
														/>
													</Text>
												) : (
													<Text>-</Text>
												)
											}
										</GetFieldValue>,
									])
								})
							}
							if (breakdown.some((r) => r.pax_type === "CNB")) {
								cnbAges.forEach((age, index) => {
									const child = findBreakdownRow(breakdown, "CNB", age)
									const fieldName = `cnb_given_rates[${index}].rate`
									const quantity = countByAge(occupancy.cnbAges, age)
									rows.push([
										<Stack gap="px">
											<Text fontSize="sm" fontWeight="semibold">
												CNB — {age}y
											</Text>
											<Text fontSize="xs" color="muted">
												{child?.occupancy}
											</Text>
										</Stack>,
										<Text fontSize="sm" color="muted">
											<Money
												currency={currency}
												amount={child?.calculated_rate_bt || 0}
											/>
										</Text>,
										<TextInputField
											name={fieldName}
											type="number"
											style={{ width: "100px" }}
											min={0}
										/>,
										quantity,
										<GetFieldValue<
											| Required<TGivenPriceFormValues>["cnb_given_rates"][number]["rate"]
											| undefined
										>
											name={fieldName}
										>
											{({ value }) =>
												value ? (
													<Text>
														<Money
															money={multipleMoneyBy(
																moneyParseByDecimal(Number(value), currency),
																quantity
															)}
														/>
													</Text>
												) : (
													<Text>-</Text>
												)
											}
										</GetFieldValue>,
									])
								})
							}
							breakdown
								.filter((r) => r.pax_type === "COMP")
								.forEach(() => {
									rows.push([
										<Text fontSize="sm" color="muted">
											Complimentary
										</Text>,
										<Text fontSize="sm" color="muted">
											Free
										</Text>,
										<Text fontSize="sm" color="muted">
											—
										</Text>,
										1,
										<Text fontSize="sm" color="muted">
											Free
										</Text>,
									])
								})

							return (
								<form noValidate onSubmit={handleSubmit}>
									<Stack gap="3">
										<Table
											bordered
											responsive
											hover
											alignCols={{ 2: "center", 3: "center", 4: "right" }}
											headers={["Component", "Rate", "Given", "Qty", "Total"]}
											rows={rows}
										>
											<tfoot>
												<FormSpy<TGivenPriceFormValues>
													subscription={{ values: true }}
												>
													{({ values }) => {
														const cwebRatesByAge = new Map(
															(values.cweb_given_rates || []).map((e) => [
																e.age,
																moneyParseByDecimal(e.rate || 0, currency),
															])
														)
														const cnbRatesByAge = new Map(
															(values.cnb_given_rates || []).map((e) => [
																e.age,
																moneyParseByDecimal(e.rate || 0, currency),
															])
														)
														const taxResult =
															computeRoomAllocationAndTaxForGivenRates(
																occupancy,
																constraints,
																{
																	baseRate: moneyParseByDecimal(
																		values.base_given_rate || 0,
																		currency
																	),
																	awebRate:
																		values.aweb_given_rate !== undefined
																			? moneyParseByDecimal(
																					values.aweb_given_rate || 0,
																					currency
																				)
																			: undefined,
																	cwebRatesByAge,
																	cnbRatesByAge,
																}
															)
														if (!taxResult) {
															return (
																<tr>
																	<TableFooterDataCell colSpan={5}>
																		<Text fontSize="sm" color="danger">
																			Enter all rates to see the total
																		</Text>
																	</TableFooterDataCell>
																</tr>
															)
														}
														return (
															<tr>
																<TableFooterDataCell colSpan={4}>
																	<Text fontWeight="semibold">
																		Total (incl. Tax)
																	</Text>
																</TableFooterDataCell>
																<TableFooterDataCell textAlign="right">
																	<Text fontWeight="semibold">
																		<Money
																			money={taxResult.winner.totalAfterTax}
																			showCurrency
																		/>
																	</Text>
																</TableFooterDataCell>
															</tr>
														)
													}}
												</FormSpy>
											</tfoot>
										</Table>
										<Stack gap="2">
											<Button
												inline
												size="sm"
												level="tertiary"
												onClick={() => setRoomAllocationExpanded((v) => !v)}
											>
												<Inline gap="2" alignItems="center">
													<Icons.ChevronDown
														style={{
															transform: roomAllocationExpanded
																? "rotate(180deg)"
																: undefined,
														}}
													/>
													<Text
														fontSize="sm"
														fontWeight="semibold"
														color="accent"
													>
														{roomAllocationExpanded ? "Hide" : "View"} Room-Wise
														Allocation
													</Text>
												</Inline>
											</Button>
											{roomAllocationExpanded ? (
												<FormSpy<TGivenPriceFormValues>
													subscription={{ values: true }}
												>
													{({ values }) => {
														const cwebRatesByAge = new Map(
															(values.cweb_given_rates || []).map((e) => [
																e.age,
																moneyParseByDecimal(e.rate || 0, currency),
															])
														)
														const cnbRatesByAge = new Map(
															(values.cnb_given_rates || []).map((e) => [
																e.age,
																moneyParseByDecimal(e.rate || 0, currency),
															])
														)
														const baseRateMoney = moneyParseByDecimal(
															values.base_given_rate || 0,
															currency
														)
														const awebRateMoney =
															values.aweb_given_rate !== undefined
																? moneyParseByDecimal(
																		values.aweb_given_rate || 0,
																		currency
																	)
																: undefined
														const taxResult =
															computeRoomAllocationAndTaxForGivenRates(
																occupancy,
																constraints,
																{
																	baseRate: baseRateMoney,
																	awebRate: awebRateMoney,
																	cwebRatesByAge,
																	cnbRatesByAge,
																}
															)
														if (!taxResult) return null

														const complimentaryAges = breakdown
															.filter((r) => r.pax_type === "COMP")
															.map((r) => r.age)
															.filter((a): a is number => a !== undefined)
														const complimentaryDist =
															distributeComplimentaryAcrossRooms(
																occupancy.noOfRooms,
																complimentaryAges,
																taxResult.winner.distribution.map(
																	(room) =>
																		room.cwebAges.length + room.cnbAges.length
																),
																constraints.maxChildOccupancy
															)

														const roomsData = taxResult.winner.distribution.map(
															(room, i) => {
																let beforeTax = baseRateMoney
																if (room.aweb && awebRateMoney) {
																	beforeTax = addMoney(
																		beforeTax,
																		multipleMoneyBy(awebRateMoney, room.aweb)
																	)
																}
																for (const age of room.cwebAges) {
																	const rate = cwebRatesByAge.get(age)
																	if (rate)
																		beforeTax = addMoney(beforeTax, rate)
																}
																for (const age of room.cnbAges) {
																	const rate = cnbRatesByAge.get(age)
																	if (rate)
																		beforeTax = addMoney(beforeTax, rate)
																}
																const taxRate = taxResult.winner.roomTaxRates[i]
																const afterTax = addPercentageToMoney(
																	beforeTax,
																	taxRate
																)
																const label = [
																	`${occupancy.personsPerRoom} Adults`,
																	room.aweb ? `${room.aweb} AWEB` : null,
																	room.cwebAges.length
																		? `CWEB (${room.cwebAges.map((a) => `${a}y`).join(", ")})`
																		: null,
																	room.cnbAges.length
																		? `CNB (${room.cnbAges.map((a) => `${a}y`).join(", ")})`
																		: null,
																	complimentaryDist[i]?.length
																		? `Comp (${complimentaryDist[i].map((a) => `${a}y`).join(", ")})`
																		: null,
																]
																	.filter(Boolean)
																	.join(" · ")
																return {
																	room,
																	i,
																	beforeTax,
																	taxRate,
																	afterTax,
																	label,
																}
															}
														)

														return (
															<Box style={{ overflowX: "auto", width: "100%" }}>
																<Table bordered responsive>
																	<thead>
																		<tr>
																			{roomsData.map(({ i, label }) => (
																				<TableHeaderDataCell
																					key={i}
																					bgColor="subtle"
																				>
																					<Stack gap="px">
																						<Text
																							fontWeight="semibold"
																							fontSize="sm"
																						>
																							Room {i + 1}
																						</Text>
																						<Text fontSize="xs" color="muted">
																							{label}
																						</Text>
																					</Stack>
																				</TableHeaderDataCell>
																			))}
																		</tr>
																	</thead>
																	<tbody>
																		<tr>
																			{roomsData.map(
																				({
																					room,
																					i,
																					beforeTax,
																					taxRate,
																					afterTax,
																				}) => (
																					<TableDataCell key={i}>
																						<Tooltip
																							content={
																								<Stack gap="1">
																									<Inline
																										justifyContent="between"
																										gap="4"
																									>
																										<Text fontSize="sm">
																											Base
																										</Text>
																										<Text fontSize="sm">
																											<Money
																												money={baseRateMoney}
																											/>
																										</Text>
																									</Inline>
																									{room.aweb &&
																									awebRateMoney ? (
																										<Inline
																											justifyContent="between"
																											gap="4"
																										>
																											<Text fontSize="sm">
																												AWEB ({room.aweb})
																											</Text>
																											<Text fontSize="sm">
																												<Money
																													money={multipleMoneyBy(
																														awebRateMoney,
																														room.aweb
																													)}
																												/>
																											</Text>
																										</Inline>
																									) : null}
																									{room.cwebAges.map((age) => (
																										<Inline
																											key={`cweb-${age}`}
																											justifyContent="between"
																											gap="4"
																										>
																											<Text fontSize="sm">
																												CWEB ({age}y)
																											</Text>
																											<Text fontSize="sm">
																												<Money
																													money={
																														cwebRatesByAge.get(
																															age
																														) ||
																														createMoney(
																															0,
																															currency
																														)
																													}
																												/>
																											</Text>
																										</Inline>
																									))}
																									{room.cnbAges.map((age) => (
																										<Inline
																											key={`cnb-${age}`}
																											justifyContent="between"
																											gap="4"
																										>
																											<Text fontSize="sm">
																												CNB ({age}y)
																											</Text>
																											<Text fontSize="sm">
																												<Money
																													money={
																														cnbRatesByAge.get(
																															age
																														) ||
																														createMoney(
																															0,
																															currency
																														)
																													}
																												/>
																											</Text>
																										</Inline>
																									))}
																									{(
																										complimentaryDist[i] || []
																									).map((age) => (
																										<Inline
																											key={`comp-${age}`}
																											justifyContent="between"
																											gap="4"
																										>
																											<Text fontSize="sm">
																												Complimentary ({age}y)
																											</Text>
																											<Text fontSize="sm">
																												Free
																											</Text>
																										</Inline>
																									))}
																									<Inline
																										justifyContent="between"
																										gap="4"
																										borderTopWidth="1"
																										paddingTop="1"
																									>
																										<Text fontSize="sm">
																											Tax ({taxRate}%)
																										</Text>
																										<Text fontSize="sm">
																											+
																											<Money
																												money={subtractMoney(
																													afterTax,
																													beforeTax
																												)}
																											/>
																										</Text>
																									</Inline>
																								</Stack>
																							}
																						>
																							<Text
																								as="span"
																								style={{
																									cursor: "help",
																									textDecoration:
																										"underline dotted",
																								}}
																							>
																								<Money
																									money={afterTax}
																									title={undefined}
																								/>
																							</Text>
																						</Tooltip>
																					</TableDataCell>
																				)
																			)}
																		</tr>
																	</tbody>
																</Table>
															</Box>
														)
													}}
												</FormSpy>
											) : null}
										</Stack>
										{hasOtherNights ? (
											<SwitchInputField
												name="keep_same_for_other_nights"
												label="Apply these rates to all other nights"
											/>
										) : null}
										<Inline gap="2">
											<Button type="submit">Save</Button>
											<Button onClick={closeDialog} level="tertiary">
												Cancel
											</Button>
										</Inline>
									</Stack>
								</form>
							)
						}}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
